<template>
  <div class="telephony-settings tel-ui">
    <p v-if="!warehouseId" class="text-sm text-gray-400">
      {{ $t('tel_select_warehouse_hint') }}
    </p>

    <div v-else class="space-y-6">
      <div v-if="loading" class="text-xs text-gray-400">{{ $t('tel_loading') }}</div>

      <!-- Enable toggle -->
      <div class="flex items-center justify-between border rounded-lg p-4 bg-gray-50">
        <div>
          <p class="font-medium text-gray-800">{{ $t('tel_enable') }}</p>
          <p class="text-xs text-gray-500">
            {{ $t('tel_enable_hint') }}
          </p>
        </div>
        <!-- Eshipi's own switch (the Tailwind-3 "peer" toggle doesn't exist in Tailwind 1.x) -->
        <AiSwitch v-model="form.enabled" size="lg" />
      </div>

      <!-- Config sections appear only once telephony is enabled -->
      <template v-if="form.enabled">

      <!-- ── Section tabs — keep the page short + scannable (each tab shows one
           area instead of stacking everything vertically). ── -->
      <nav class="tel-tabs">
        <button type="button" v-for="t in telTabs" :key="t.id"
          class="tel-tab" :class="{ 'tel-tab--active': activeTab === t.id }"
          @click="activeTab = t.id">
          <Icon :icon="t.icon" class="text-lg" />
          <span>{{ $t(t.label) }}</span>
        </button>
      </nav>

      <!-- ══ TAB: Mode d'appel ══ -->
      <div v-show="activeTab === 'mode'" class="tel-tabpane">

      <!-- ── Call mode (per warehouse) ── manual vs auto-dialer campaign. -->
      <div class="border rounded-lg p-4">
        <div class="flex items-center gap-2 mb-3">
          <h3 class="font-semibold text-gray-800">{{ $t('tel_callmode_title') }}</h3>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label
            class="flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition"
            :class="form.callMode === 'manual' ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200' : 'border-gray-200 hover:border-gray-300'"
          >
            <input type="radio" value="manual" v-model="form.callMode" class="mt-1" />
            <span>
              <span class="block font-medium text-slate-800">{{ $t('tel_callmode_manual') }}</span>
              <span class="block text-xs text-slate-500 mt-0.5">{{ $t('tel_callmode_manual_hint') }}</span>
            </span>
          </label>
          <label
            class="flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition"
            :class="form.callMode === 'auto' ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200' : 'border-gray-200 hover:border-gray-300'"
          >
            <input type="radio" value="auto" v-model="form.callMode" class="mt-1" />
            <span>
              <span class="block font-medium text-slate-800">{{ $t('tel_callmode_auto') }}</span>
              <span class="block text-xs text-slate-500 mt-0.5">{{ $t('tel_callmode_auto_hint') }}</span>
            </span>
          </label>
          <label
            class="flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition"
            :class="form.callMode === 'predictive' ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200' : 'border-gray-200 hover:border-gray-300'"
          >
            <input type="radio" value="predictive" v-model="form.callMode" class="mt-1" />
            <span>
              <span class="block font-medium text-slate-800">{{ $t('tel_callmode_predictive') }}</span>
              <span class="block text-xs text-slate-500 mt-0.5">{{ $t('tel_callmode_predictive_hint') }}</span>
            </span>
          </label>
        </div>

        <!-- Auto-dialer knobs (only when auto) -->
        <div v-if="form.callMode === 'auto'" class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t">
          <div>
            <label class="lbl">{{ $t('tel_ad_delay') }}</label>
            <input v-model.number="form.autoDialer.delayBetweenCalls" type="number" min="0" max="60" class="inp" />
            <p class="mt-1 text-[11px] text-slate-500">{{ $t('tel_ad_delay_hint') }}</p>
          </div>
          <div>
            <label class="lbl">{{ $t('tel_ad_recheck') }}</label>
            <input v-model.number="form.autoDialer.recheckEmptyMins" type="number" min="1" max="30" class="inp" />
            <p class="mt-1 text-[11px] text-slate-500">{{ $t('tel_ad_recheck_hint') }}</p>
          </div>
        </div>

        <!-- waitForClientAnswer toggle -->
        <div v-if="form.callMode === 'auto'" class="mt-3">
          <label class="inline-flex items-start gap-2 cursor-pointer">
            <input type="checkbox" v-model="form.autoDialer.waitForClientAnswer" class="mt-0.5" />
            <span>
              <span class="text-sm font-medium text-slate-700">{{ $t('tel_ad_wait_answer') }}</span>
              <span class="block text-xs text-slate-500 mt-0.5">{{ $t('tel_ad_wait_answer_hint') }}</span>
            </span>
          </label>
        </div>

        <!-- Predictive/queue config (only when predictive) -->
        <div v-if="form.callMode === 'predictive'" class="mt-4 pt-4 border-t space-y-4">
          <p class="text-xs text-slate-500">{{ $t('tel_pd_intro') }}</p>

          <!-- Wait message. EMPTY at first: no player — just "Apply default voice"
               + "Upload MP3". Once a message is set (default applied OR custom
               uploaded) the player appears so the manager can hear exactly what the
               client hears, plus Replace / revert-to-default. -->
          <div>
            <label class="lbl">{{ $t('tel_pd_waitmsg') }}</label>

            <!-- STATE A — nothing set yet: preview the default voice, then Apply it
                 or Upload your own. -->
            <div v-if="!form.predictiveDialer.waitMessageUrl" class="flex flex-wrap items-center gap-2">
              <!-- Default voice: only offered when one is configured (defaultWaitMsgUrl). -->
              <template v-if="defaultWaitMsgUrl">
                <!-- Listen to the default voice BEFORE applying it. -->
                <audio :src="defaultWaitMsgUrl" controls preload="none" class="h-8"></audio>
                <button type="button"
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50"
                  @click="applyDefaultWaitMsg" :disabled="uploadingWaitMsg">
                  <Icon icon="mdi:account-voice" class="text-base" />
                  {{ $t('tel_pd_apply_default') }}
                </button>
              </template>
              <label class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-sky-300 bg-white text-sky-700 hover:bg-sky-50 cursor-pointer">
                <Icon :icon="uploadingWaitMsg ? 'eos-icons:three-dots-loading' : 'mdi:upload'" class="text-base" />
                {{ $t('tel_pd_upload') }}
                <input type="file" accept="audio/mpeg,.mp3" class="hidden" @change="onWaitMsgUpload" :disabled="uploadingWaitMsg" />
              </label>
            </div>

            <!-- STATE B — a message is active: play it + replace / revert. -->
            <div v-else class="flex flex-wrap items-center gap-2">
              <audio :src="form.predictiveDialer.waitMessageUrl" controls preload="none" class="h-8"></audio>
              <label class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-sky-300 bg-white text-sky-700 hover:bg-sky-50 cursor-pointer">
                <Icon :icon="uploadingWaitMsg ? 'eos-icons:three-dots-loading' : 'mdi:upload'" class="text-base" />
                {{ $t('tel_pd_replace') }}
                <input type="file" accept="audio/mpeg,.mp3" class="hidden" @change="onWaitMsgUpload" :disabled="uploadingWaitMsg" />
              </label>
              <button type="button"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-50"
                @click="pushWaitMsgToBoxes" :disabled="syncingWaitMsg"
                :title="$t('tel_pd_waitmsg_sync_hint')">
                <Icon :icon="syncingWaitMsg ? 'eos-icons:three-dots-loading' : 'mdi:cloud-sync'" class="text-base" />
                {{ $t('tel_pd_waitmsg_sync_btn') }}
              </button>
              <button type="button" class="text-[11px] text-red-600 hover:underline" @click="removeWaitMsg">
                {{ $t('tel_pd_remove') }}
              </button>
            </div>

            <p class="mt-1 text-[11px] text-slate-500">
              {{ form.predictiveDialer.waitMessageUrl
                  ? (isDefaultWaitMsg ? $t('tel_pd_waitmsg_default_active') : $t('tel_pd_waitmsg_custom_active'))
                  : (defaultWaitMsgUrl ? $t('tel_pd_waitmsg_none') : $t('tel_pd_waitmsg_none_upload')) }}
            </p>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="lbl">{{ $t('tel_pd_maxwait') }}</label>
              <input v-model.number="form.predictiveDialer.maxWaitSecs" type="number" min="30" max="900" class="inp" />
              <p class="mt-1 text-[11px] text-slate-500">{{ $t('tel_pd_maxwait_hint') }}</p>
            </div>
            <div>
              <label class="lbl">{{ $t('tel_pd_delay') }}</label>
              <input v-model.number="form.predictiveDialer.delayBetweenCalls" type="number" min="0" max="60" class="inp" />
              <p class="mt-1 text-[11px] text-slate-500">{{ $t('tel_pd_delay_hint') }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Animated guide: how each call mode works (opens in a modal so it
           doesn't push the whole settings page down) ── -->
      <div class="border rounded-lg p-4">
        <button
          type="button"
          class="flex items-center gap-3 w-full text-left group"
          @click="openGuide"
        >
          <span class="flex-shrink-0 w-9 h-9 rounded-lg bg-sky-50 text-sky-500 flex items-center justify-center group-hover:bg-sky-100 transition">
            <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          </span>
          <span class="flex-1">
            <span class="block font-semibold text-gray-800">{{ $t('tg_open') }}</span>
            <span class="block text-xs text-slate-500 mt-0.5">{{ $t('tg_sub') }}</span>
          </span>
          <svg class="w-4 h-4 text-slate-400 group-hover:text-sky-500 transition" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
        </button>
      </div>

      </div><!-- /TAB mode -->

      <!-- The guide modal (rendered once, OUTSIDE the tabs so it opens from anywhere). -->
      <vue-modal-2
        name="TelephonyGuideModal"
        :headerOptions="{ title: $t('tg_title') }"
        modalSize="xl"
        :noFooter="true"
        @on-close="closeGuide"
      >
        <div class="p-5 bg-white rounded-b-lg max-h-[80vh] overflow-y-auto">
          <TelephonyGuide v-if="guideOpen" />
        </div>
      </vue-modal-2>

      <!-- ══ TAB: Extensions (agents + test call) ══ -->
      <div v-show="activeTab === 'ext'" class="tel-tabpane">

      <!-- Extensions check — opens a modal (agents with / without an extension,
           generate + enable/disable). -->
      <div class="border rounded-lg p-4">
        <button type="button" class="flex items-center gap-3 w-full text-left group" @click="openExtModal">
          <span class="flex-shrink-0 w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center group-hover:bg-indigo-100 transition">
            <Icon icon="mdi:phone-check" class="text-xl" />
          </span>
          <span class="flex-1">
            <span class="block font-semibold text-gray-800">{{ $t('tel_ext_title') }}</span>
            <span class="block text-xs text-slate-500 mt-0.5">{{ $t('tel_ext_intro') }}</span>
          </span>
          <svg class="w-4 h-4 text-slate-400 group-hover:text-indigo-500 transition" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
        </button>
      </div>

      <!-- Extensions modal -->
      <vue-modal-2
        name="ExtensionsModal"
        :headerOptions="{ title: $t('tel_ext_title') }"
        modalSize="lg"
        :noFooter="true"
        @on-close="$vm2.close('ExtensionsModal')"
      >
        <!-- Fixed-height flex column: header + tabs stay put, ONLY the list scrolls
             (avoids the double scrollbar). -->
        <div class="bg-white rounded-b-lg flex flex-col" style="height:70vh">
          <!-- Header (fixed) -->
          <div class="flex items-center justify-between px-5 pt-4 pb-3 flex-shrink-0">
            <p class="text-xs text-slate-500">{{ $t('tel_ext_intro') }}</p>
            <button type="button" :disabled="checkingExts"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
              @click="checkExtensions">
              <Icon :icon="checkingExts ? 'eos-icons:three-dots-loading' : 'mdi:refresh'" class="text-base" />
              {{ $t('tel_ext_run') }}
            </button>
          </div>

          <div v-if="checkingExts && !extResult" class="flex-1 flex items-center justify-center text-slate-400">
            <Icon icon="eos-icons:three-dots-loading" class="text-3xl" />
          </div>

          <template v-else-if="extResult">
            <div v-if="!extResult.ok" class="px-5 text-red-600 text-sm">{{ extResult.error || $t('tel_ext_failed') }}</div>
            <template v-else>
              <!-- Summary + Tabs (fixed) -->
              <div class="px-5 flex-shrink-0">
                <p class="text-sm font-medium mb-3"
                   :class="extResult.missing ? 'text-red-700' : (extResult.offline ? 'text-amber-700' : 'text-green-700')">
                  {{ extResult.missing
                       ? ('✗ ' + $t('tel_ext_missing_n', { n: extResult.missing }))
                       : (extResult.offline
                            ? ('! ' + $t('tel_ext_offline_n', { n: extResult.offline, t: extResult.total }))
                            : ('✓ ' + $t('tel_ext_all_ok', { t: extResult.total }))) }}
                </p>
                <div class="flex gap-1 border-b border-slate-200">
                  <button type="button"
                    class="px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition"
                    :class="extTab === 'with' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'"
                    @click="extTab = 'with'">
                    <Icon icon="mdi:phone-in-talk" class="inline align-middle" /> {{ $t('tel_ext_sec_with') }} ({{ extResult.total }})
                  </button>
                  <button type="button"
                    class="px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition"
                    :class="extTab === 'without' ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'"
                    @click="extTab = 'without'">
                    <Icon icon="mdi:phone-plus" class="inline align-middle" /> {{ $t('tel_ext_sec_without') }} ({{ (extResult.withoutExtension || []).length }})
                  </button>
                </div>
              </div>

              <!-- Scroll area — the ONLY scrollbar in the modal -->
              <div class="flex-1 overflow-y-auto px-5 pt-3 pb-4">
                <!-- TAB: agents WITH an extension -->
                <div v-if="extTab === 'with'">
                  <div v-if="!extResult.total" class="text-xs text-slate-400 py-6 text-center">{{ $t('tel_ext_none') }}</div>
                  <table v-else class="w-full text-[11px]">
                    <thead>
                      <tr class="text-slate-400 text-left sticky top-0 bg-white">
                        <th class="py-1 pr-2">{{ $t('tel_ext_col_ext') }}</th>
                        <th class="py-1 pr-2">{{ $t('tel_ext_col_agent') }}</th>
                        <th class="py-1 pr-2">{{ $t('tel_ext_col_country') }}</th>
                        <th class="py-1 pr-2">{{ $t('tel_ext_col_status') }}</th>
                        <th class="py-1 text-right">{{ $t('tel_ext_col_action') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="e in extResult.extensions" :key="e.userId" class="border-t border-slate-100">
                        <td class="py-1.5 pr-2 font-mono font-semibold text-slate-700">{{ e.extension }}</td>
                        <td class="py-1.5 pr-2">
                          <div class="text-slate-700 font-medium truncate">{{ e.agent || '—' }}</div>
                          <div v-if="e.username && e.username !== e.agent" class="text-slate-400 truncate">{{ e.username }}</div>
                        </td>
                        <td class="py-1.5 pr-2 text-slate-500 whitespace-nowrap">
                          <template v-if="e.countries && e.countries.length">
                            <span v-for="c in e.countries" :key="c" class="mr-1" :title="c">{{ flagEmoji(c) }}</span>
                          </template>
                          <span v-else>—</span>
                        </td>
                        <td class="py-1.5 pr-2 whitespace-nowrap">
                          <span v-if="e.exists === false" class="text-red-600">✗ {{ $t('tel_ext_not_created') }}</span>
                          <span v-else-if="!e.activeInCrm" class="text-slate-400">⚪ {{ $t('tel_ext_disabled') }}</span>
                          <span v-else-if="e.online" class="text-green-600">🟢 {{ $t('tel_ext_online') }}</span>
                          <span v-else class="text-amber-600">⚪ {{ $t('tel_ext_offline') }}</span>
                        </td>
                        <td class="py-1.5 text-right whitespace-nowrap">
                          <button v-if="e.exists === false" type="button"
                            :disabled="extActionId === e.userId"
                            class="text-[11px] px-2 py-0.5 rounded bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
                            @click="genExtension(e)">{{ $t('tel_ext_recreate') }}</button>
                          <button v-else-if="e.activeInCrm" type="button"
                            :disabled="extActionId === e.userId"
                            class="text-[11px] px-2 py-0.5 rounded bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                            @click="toggleExtActive(e, false)">{{ $t('tel_ext_deactivate') }}</button>
                          <button v-else type="button"
                            :disabled="extActionId === e.userId"
                            class="text-[11px] px-2 py-0.5 rounded bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50"
                            @click="toggleExtActive(e, true)">{{ $t('tel_ext_activate') }}</button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- TAB: agents WITHOUT an extension -->
                <div v-else>
                  <div v-if="!(extResult.withoutExtension || []).length" class="text-xs text-slate-400 py-6 text-center">{{ $t('tel_ext_none_without') }}</div>
                  <table v-else class="w-full text-[11px]">
                    <thead>
                      <tr class="text-slate-400 text-left sticky top-0 bg-white">
                        <th class="py-1 pr-2">{{ $t('tel_ext_col_agent') }}</th>
                        <th class="py-1 pr-2">{{ $t('tel_ext_col_country') }}</th>
                        <th class="py-1 text-right">{{ $t('tel_ext_col_action') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="a in extResult.withoutExtension" :key="a.userId" class="border-t border-slate-100">
                        <td class="py-1.5 pr-2">
                          <div class="text-slate-700 font-medium truncate">{{ a.agent || '—' }}</div>
                          <div v-if="a.username && a.username !== a.agent" class="text-slate-400 truncate">{{ a.username }}</div>
                        </td>
                        <td class="py-1.5 pr-2 whitespace-nowrap">
                          <span v-for="c in a.countries" :key="c" class="mr-1">{{ flagEmoji(c) }}</span>
                        </td>
                        <td class="py-1.5 text-right">
                          <button type="button"
                            :disabled="extActionId === a.userId"
                            class="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                            @click="genExtension(a)">
                            <Icon :icon="extActionId === a.userId ? 'eos-icons:three-dots-loading' : 'mdi:plus'" />
                            {{ $t('tel_ext_generate') }}
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p class="mt-3 text-[11px] text-slate-400">{{ $t('tel_ext_note') }}</p>
              </div>
            </template>
          </template>
        </div>
      </vue-modal-2>

      <!-- Dongle test-call modal MOVED out of the tab panes → see below the Save
           button. It is opened from the "Passerelles" tab (📞 Test call) but the
           modal markup itself must NOT sit inside a `v-show` tab pane, or the pane's
           display:none hides the whole modal when it opens (it opened but was
           invisible — no error). Kept at the always-rendered component root. -->


      </div><!-- /TAB ext -->

      <!-- ══ TAB: Serveur PBX ══ -->
      <div v-show="activeTab === 'pbx'" class="tel-tabpane">

      <!-- PBX (Issabel server) — GLOBAL online VPS, shared by all warehouses.
           NOT a dongle mini-PC: those are per-warehouse, set on each gateway. -->
      <div class="border rounded-lg p-4">
        <div class="flex items-center gap-2 mb-3">
          <h3 class="font-semibold text-gray-800">{{ $t('tel_pbx_title') }}</h3>
          <span class="text-[11px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{{ $t('tel_global_badge') }}</span>
          <button
            type="button"
            @click="showPbxGuide = !showPbxGuide"
            :title="$t('tel_pbx_guide_title')"
            class="flex items-center justify-center w-5 h-5 rounded-full border border-blue-300 text-blue-600 text-xs font-bold hover:bg-blue-50"
          >?</button>
        </div>

        <!-- PBX fields guide (collapsible) -->
        <div v-if="showPbxGuide" class="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-100 text-sm text-slate-700 space-y-1.5">
          <p class="font-semibold text-blue-800 mb-1">{{ $t('tel_pbx_guide_title') }}</p>
          <p><b>Host / IP</b> — {{ $t('tel_pbx_guide_host') }}</p>
          <p><b>Port ARI</b> — {{ $t('tel_pbx_guide_port') }}</p>
          <p><b>ARI User</b> — {{ $t('tel_pbx_guide_ariuser') }}</p>
          <p class="text-xs text-slate-500">{{ $t('tel_pbx_guide_note') }}</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="lbl">Host / IP</label>
            <input v-model="form.pbx.host" class="inp" placeholder="192.168.100.244" />
          </div>
          <div>
            <label class="lbl">Port ARI</label>
            <input v-model.number="form.pbx.ariPort" type="number" class="inp" placeholder="8088" />
          </div>
          <div>
            <label class="lbl">ARI User</label>
            <input v-model="form.pbx.ariUser" class="inp" />
          </div>
          <div class="flex items-end gap-2">
            <button @click="testConnection" :disabled="testing" class="btn-secondary">
              {{ testing ? $t('tel_testing') : $t('tel_test_connection') }}
            </button>
            <!-- Full VPS-hub health: ARI reachable+auth, trunk to the mini-PC
                 online, WebRTC agents registered, WSS cert valid. -->
            <button
              type="button"
              @click="checkVpsHub"
              :disabled="checkingVps"
              :title="$t('tel_vps_check_hint')"
              class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border violet-button border-violet-600 bg-violet-600 text-white shadow-sm hover:bg-violet-700 hover:border-violet-700 active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>🛰️</span>
              {{ checkingVps ? $t('tel_testing') : $t('tel_vps_check_btn') }}
            </button>
            <!-- Push the CRM config (API url + webhook secret + projectId) from
                 telephonyConfig.js into every box's /etc/asterisk/crm-box.conf, so
                 changing those values follows automatically (no manual sed). -->
            <button
              type="button"
              @click="syncConfigToBoxes"
              :disabled="syncingConfig"
              :title="$t('tel_sync_config_hint')"
              class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-700 shadow-sm hover:bg-slate-50 active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>🔄</span>
              {{ syncingConfig ? $t('tel_testing') : $t('tel_sync_config_btn') }}
            </button>
          </div>
        </div>
        <!-- Unified health/result card (Test connection · Check VPS · Sync config).
             ONE panel at a time (handlers clear the others). -->
        <TelHealthCard v-if="healthPanel" class="mt-3" :panel="healthPanel" />
        <p class="mt-2 text-xs text-slate-400">
          🔒 {{ $t('tel_secrets_note') }}
        </p>
      </div>

      </div><!-- /TAB pbx -->

      <!-- ══ TAB: Passerelles (GoIP / mini-PC + dongles) ══ -->
      <div v-show="activeTab === 'gateways'" class="tel-tabpane">

      <!-- Gateways (GoIP) -->
      <div class="border rounded-lg p-4">
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center gap-2">
            <h3 class="font-semibold text-gray-800">{{ $t('tel_gateways_title') }}</h3>
            <span class="text-[11px] bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full">
              {{ $t('tel_warehouse_specific') }}{{ warehouse && warehouse.name ? ' — ' + warehouse.name : '' }}
            </span>
            <!-- Help: how to set up a GoIP for this warehouse -->
            <button
              type="button"
              @click="showGuide = !showGuide"
              :title="$t('tel_guide_title')"
              class="flex items-center justify-center w-5 h-5 rounded-full border border-blue-300 text-blue-600 text-xs font-bold hover:bg-blue-50"
            >?</button>
            <!-- Explainer: how a call travels agent → client (for managers) -->
            <button
              type="button"
              @click="showFlow = !showFlow"
              class="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border border-indigo-200 text-indigo-600 hover:bg-indigo-50 transition"
            >
              <span>🔎</span>{{ $t('tel_callflow_btn') }}
            </button>
          </div>
          <button @click="addGateway" class="btn-secondary">{{ $t('tel_add_gateway') }}</button>
        </div>

        <!-- ══ How it works: the path of a call (agent → hub → gateway → client) ══ -->
        <transition name="fade">
        <div v-if="showFlow" class="tel-flow mb-4">
          <!-- header -->
          <div class="tel-flow__head">
            <div>
              <div class="tel-flow__eyebrow">Call-Center · {{ $t('tel_callflow_btn') }}</div>
              <h4 class="tel-flow__title">{{ $t('tel_callflow_title') }}</h4>
              <p class="tel-flow__sub">{{ $t('tel_callflow_sub') }}</p>
            </div>
            <button type="button" @click="showFlow = false" class="tel-flow__close" aria-label="close">×</button>
          </div>

          <!-- flow diagram -->
          <div class="tel-flow__stage overflow-x-auto">
            <svg viewBox="0 0 900 210" class="w-full min-w-[660px]" role="img" :aria-label="$t('tel_callflow_title')">
              <defs>
                <marker id="tgArr" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                  <path d="M0 0 L10 5 L0 10 z" fill="#2563c9"/>
                </marker>
                <linearGradient id="tgLane" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stop-color="#1f8a54" stop-opacity=".18"/>
                  <stop offset=".5" stop-color="#2563c9" stop-opacity=".18"/>
                  <stop offset="1" stop-color="#c2681a" stop-opacity=".18"/>
                </linearGradient>
              </defs>
              <!-- lane -->
              <rect x="30" y="103" width="840" height="4" rx="2" fill="url(#tgLane)"/>
              <!-- nodes: soft fill + colored ring + emoji badge -->
              <g>
                <rect x="16" y="58" width="156" height="94" rx="16" fill="#f0f9f3" stroke="#1f8a54" stroke-width="1.4"/>
                <circle cx="94" cy="88" r="19" fill="#e7f4ec"/>
                <text x="94" y="95" text-anchor="middle" font-size="20">🎧</text>
                <text x="94" y="123" text-anchor="middle" font-size="13.5" font-weight="700" fill="#16202b">{{ $t('tel_callflow_n_agent') }}</text>
                <text x="94" y="140" text-anchor="middle" font-size="10.5" fill="#4a5a6a">{{ $t('tel_callflow_n_agent_s') }}</text>
              </g>
              <g>
                <rect x="258" y="58" width="156" height="94" rx="16" fill="#eef5fe" stroke="#2563c9" stroke-width="1.4"/>
                <circle cx="336" cy="88" r="19" fill="#e6effc"/>
                <text x="336" y="95" text-anchor="middle" font-size="20">☁️</text>
                <text x="336" y="123" text-anchor="middle" font-size="13.5" font-weight="700" fill="#16202b">{{ $t('tel_callflow_n_hub') }}</text>
                <text x="336" y="140" text-anchor="middle" font-size="10.5" fill="#4a5a6a">{{ $t('tel_callflow_n_hub_s') }}</text>
              </g>
              <g>
                <rect x="500" y="58" width="156" height="94" rx="16" fill="#f5f0fd" stroke="#7c3aed" stroke-width="1.4"/>
                <circle cx="578" cy="88" r="19" fill="#efe8fc"/>
                <text x="578" y="95" text-anchor="middle" font-size="20">📟</text>
                <text x="578" y="123" text-anchor="middle" font-size="13.5" font-weight="700" fill="#16202b">{{ $t('tel_callflow_n_gw') }}</text>
                <text x="578" y="140" text-anchor="middle" font-size="10.5" fill="#4a5a6a">{{ $t('tel_callflow_n_gw_s') }}</text>
              </g>
              <g>
                <rect x="742" y="58" width="156" height="94" rx="16" fill="#fdf4e9" stroke="#c2681a" stroke-width="1.4"/>
                <circle cx="820" cy="88" r="19" fill="#fbeedd"/>
                <text x="820" y="95" text-anchor="middle" font-size="20">📱</text>
                <text x="820" y="123" text-anchor="middle" font-size="13.5" font-weight="700" fill="#16202b">{{ $t('tel_callflow_n_client') }}</text>
                <text x="820" y="140" text-anchor="middle" font-size="10.5" fill="#4a5a6a">{{ $t('tel_callflow_n_client_s') }}</text>
              </g>
              <!-- arrows (two-way) with pill labels -->
              <line x1="174" y1="105" x2="254" y2="105" stroke="#2563c9" stroke-width="2" marker-end="url(#tgArr)" marker-start="url(#tgArr)"/>
              <rect x="182" y="34" width="66" height="20" rx="10" fill="#e6effc"/>
              <text x="215" y="48" text-anchor="middle" font-size="11" font-weight="600" fill="#2563c9">WebRTC</text>
              <line x1="416" y1="105" x2="496" y2="105" stroke="#2563c9" stroke-width="2" marker-end="url(#tgArr)" marker-start="url(#tgArr)"/>
              <rect x="422" y="34" width="70" height="20" rx="10" fill="#e6effc"/>
              <text x="457" y="48" text-anchor="middle" font-size="11" font-weight="600" fill="#2563c9">Trunk SIP</text>
              <line x1="658" y1="105" x2="738" y2="105" stroke="#c2681a" stroke-width="2" marker-end="url(#tgArr)" marker-start="url(#tgArr)"/>
              <rect x="660" y="34" width="76" height="20" rx="10" fill="#fbeedd"/>
              <text x="698" y="48" text-anchor="middle" font-size="10.5" font-weight="600" fill="#c2681a">{{ $t('tel_callflow_gsm') }}</text>
              <!-- two-way note -->
              <text x="457" y="190" text-anchor="middle" font-size="11.5" fill="#4a5a6a">↔ {{ $t('tel_callflow_2way') }}</text>
            </svg>
          </div>

          <!-- the two gateway kinds — accent-topped cards like the reference -->
          <div class="tel-flow__cards">
            <div class="tel-flow__card">
              <div class="tel-flow__card-h">📟 {{ $t('tel_callflow_goip_t') }}</div>
              <p>{{ $t('tel_callflow_goip_d') }}</p>
            </div>
            <div class="tel-flow__card">
              <div class="tel-flow__card-h">💻 {{ $t('tel_callflow_dongle_t') }}</div>
              <p>{{ $t('tel_callflow_dongle_d') }}</p>
            </div>
          </div>
          <p class="tel-flow__tip">💡 {{ $t('tel_callflow_capacity') }}</p>
        </div>
        </transition>

        <!-- Gateway-kind selector — the setup guide below shows the exact steps
             for the selected kind. GoIP brands (Dinstar/OpenVox) have web-UI
             menus; "Dongle (Huawei)" is the DIY mini-PC + chan_dongle gateway. -->
        <div class="mb-3 flex items-center gap-2">
          <span class="text-xs text-slate-500">{{ $t('tel_brand_label') }}</span>
          <div class="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
            <button
              type="button"
              class="px-3 py-1 rounded-md text-xs font-medium transition"
              :class="guideBrand === 'dinstar' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'"
              @click="guideBrand = 'dinstar'"
            >Dinstar</button>
            <button
              type="button"
              class="px-3 py-1 rounded-md text-xs font-medium transition"
              :class="guideBrand === 'openvox' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'"
              @click="guideBrand = 'openvox'"
            >OpenVox</button>
            <button
              type="button"
              class="px-3 py-1 rounded-md text-xs font-medium transition"
              :class="guideBrand === 'huawei' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'"
              @click="guideBrand = 'huawei'"
            >{{ $t('tel_brand_dongle') }}</button>
          </div>
        </div>

        <!-- Setup guide (collapsible) — uses this warehouse's suggested trunk
             name and the configured PBX host so the values are copy-ready. -->
        <div v-if="showGuide" class="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-100 text-sm text-slate-700 space-y-3">
          <p class="font-semibold text-blue-800">
            {{ $t('tel_guide_title') }}<template v-if="warehouseName"> — {{ warehouseName }}</template>
          </p>

          <!-- ════ GoIP setup (Dinstar / OpenVox) ════ -->
          <template v-if="!isDongleGuide">
          <!-- 1. FreePBX trunk -->
          <div>
            <p class="font-medium">1. {{ $t('tel_guide_s1_title') }}</p>
            <p class="text-xs text-slate-500">Connectivity → Trunks → Add SIP (chan_pjsip) Trunk</p>
            <div class="mt-1 font-mono text-xs bg-white rounded border p-2 space-y-0.5">
              <div>Trunk Name / Username: <b>{{ suggestedTrunk }}</b></div>
              <div>Secret: &lt;{{ $t('tel_guide_password') }}&gt;</div>
              <div>Authentication: Inbound &nbsp;·&nbsp; Registration: Receive</div>
              <div>Context: <b>crm-inbound</b></div>
              <div class="pt-1 text-amber-700">⚠️ identify_by=auth_username,username,ip</div>
            </div>
            <p class="mt-1 text-xs text-slate-500">{{ $t('tel_guide_s1_identify') }}</p>
          </div>

          <!-- 2. GoIP register (brand-specific menu names) -->
          <div>
            <p class="font-medium">2. {{ $t('tel_guide_s2_title') }}</p>
            <!-- Dinstar -->
            <div v-if="guideBrand === 'dinstar'" class="mt-1 font-mono text-xs bg-white rounded border p-2 space-y-0.5">
              <div>SIP → SIP Endpoints (Server) : <b>{{ pbxHostDisplay }}</b> : 5060</div>
              <div>SIP User ID / Authenticate ID: <b>{{ suggestedTrunk }}</b></div>
              <div>Password: &lt;{{ $t('tel_guide_same_secret') }}&gt;</div>
              <div>Register: Enable</div>
            </div>
            <!-- Dinstar OUTBOUND SIM spread (multi-agent → one SIM each) -->
            <div v-if="guideBrand === 'dinstar'" class="mt-1.5 font-mono text-xs bg-white rounded border p-2 space-y-0.5">
              <div class="text-slate-500 font-sans mb-0.5">{{ $t('tel_guide_s2_outbound') }}</div>
              <div>Port Group Config → Port: <b>0,1,2,…</b> (all SIMs)</div>
              <div>Port Group → Select Mode: <b>Cyclic Ascending</b></div>
              <div>IP→Tel Routing: SIP Server → <b>Port Group-0</b></div>
            </div>
            <!-- OpenVox (VS_USB-1020 web UI) -->
            <div v-else class="mt-1 font-mono text-xs bg-white rounded border p-2 space-y-0.5">
              <div>VOIP → VoIP Endpoints → <b>Add New SIP Endpoint</b></div>
              <div>Registration: <b>Client</b> &nbsp;(gateway registers to the PBX)</div>
              <div>Hostname or IP · Port: <b>{{ pbxHostDisplay }}</b> : 5060</div>
              <div>User Name · Authentication User: <b>{{ suggestedTrunk }}</b></div>
              <div>Password: &lt;{{ $t('tel_guide_same_secret') }}&gt;</div>
              <div>Directmedia: <b>No</b> &nbsp;(so recording works)</div>
              <div>NETWORK → LAN Settings: Default Gateway = your router IP</div>
            </div>
            <p class="mt-1 text-xs text-slate-500">{{ $t('tel_guide_s2_note') }}</p>
          </div>

          <!-- 3. CRM gateway -->
          <div>
            <p class="font-medium">3. {{ $t('tel_guide_s3_title') }}</p>
            <p class="text-xs">
              {{ $t('tel_guide_s3_desc') }}
              <code class="px-1 bg-white rounded border">{{ suggestedTrunk }}</code>
            </p>
          </div>

          <!-- 4. check -->
          <p class="font-medium">4. {{ $t('tel_guide_s4') }}</p>
          <p class="text-xs text-slate-500">⚠️ {{ $t('tel_guide_note') }}</p>
          </template>

          <!-- ════ GSM Dongle setup (Huawei + chan_dongle mini-PC) ════ -->
          <template v-else>
          <p class="text-xs text-slate-600">{{ $t('tel_dg_intro') }}</p>

          <!-- 1. mini-PC + dongles -->
          <div>
            <p class="font-medium">1. {{ $t('tel_dg_s1_title') }}</p>
            <div class="mt-1 font-mono text-xs bg-white rounded border p-2 space-y-0.5">
              <div>{{ $t('tel_dg_s1_hw') }}</div>
              <div>Issabel + <b>chan_dongle</b> · Huawei <b>E173/E153/E1750</b></div>
              <div class="text-amber-700">⚠️ {{ $t('tel_dg_s1_power') }}</div>
              <div class="text-amber-700">⚠️ {{ $t('tel_dg_s1_udev') }}</div>
            </div>
            <p class="mt-1 text-xs text-slate-500">{{ $t('tel_dg_s1_ref') }}</p>
          </div>

          <!-- 2. PBX host = the mini-PC (via Tailscale) -->
          <div>
            <p class="font-medium">2. {{ $t('tel_dg_s2_title') }}</p>
            <div class="mt-1 font-mono text-xs bg-white rounded border p-2 space-y-0.5">
              <div>PBX Host: <b>{{ pbxHostDisplay }}</b> &nbsp;(Tailscale 100.x)</div>
              <div>ARI Port: <b>8088</b> · ARI User: <b>crm</b></div>
              <div>{{ $t('tel_dg_s2_ext') }}</div>
            </div>
            <!-- Network gotchas that cost the most time — see the server doc §4b. -->
            <div class="mt-1.5 rounded border border-amber-200 bg-amber-50/60 p-2 space-y-0.5">
              <div class="text-amber-800 font-medium">⚠️ {{ $t('tel_dg_net_title') }}</div>
              <div class="text-xs text-slate-600">• {{ $t('tel_dg_net_ts') }}</div>
              <div class="text-xs text-slate-600">• {{ $t('tel_dg_net_ip') }}</div>
              <div class="text-xs text-slate-600">• {{ $t('tel_dg_net_iptables') }}</div>
              <div class="text-xs text-slate-600">• {{ $t('tel_dg_net_scp') }}</div>
              <div class="text-xs text-slate-600">• {{ $t('tel_dg_net_deploy') }}</div>
            </div>
          </div>

          <!-- 3. CRM gateway -->
          <div>
            <p class="font-medium">3. {{ $t('tel_dg_s3_title') }}</p>
            <div class="mt-1 font-mono text-xs bg-white rounded border p-2 space-y-0.5">
              <div>Type: <b>{{ $t('tel_type_dongle') }}</b> · Brand: <b>Huawei</b></div>
              <div>{{ $t('tel_dongles_count') }}: <b>3</b> &nbsp;(= dongles)</div>
              <div>Trunk (group): <b>g0</b></div>
            </div>
            <!-- Auto-trunk: the SIP trunk that links the mini-PC to the VPS hub no
                 longer needs a manual pjsip_custom.conf edit on both boxes — after
                 saving the gateway (trunk name + mini-PC host), the 🔗 "Créer le
                 trunk" button below writes both sides + reloads; 🔎 verifies it. -->
            <p class="mt-1.5 text-xs text-violet-700 bg-violet-50 border border-violet-100 rounded px-2 py-1.5">
              🔗 {{ $t('tel_dg_trunk_guide_note') }}
            </p>
          </div>

          <!-- 3b. Voice / audio prerequisites — the hard-won lessons. A dongle
               that shows "Free" can still be SILENT unless these hold. Surfaced
               here so a new mini-PC is set up right the first time. Full detail:
               backend/docs/dongle-chan-dongle-audio-fix.md + backend/docs/gsm-dongle-gateway.md. -->
          <div>
            <p class="font-medium">{{ $t('tel_dg_voice_title') }}</p>
            <div class="mt-1 rounded border border-emerald-200 bg-emerald-50/60 p-2 space-y-1">
              <div class="text-xs text-slate-700">✅ {{ $t('tel_dg_voice_patch') }}</div>
              <div class="text-xs text-slate-700">✅ {{ $t('tel_dg_voice_tty') }}</div>
              <div class="text-xs text-slate-700">✅ {{ $t('tel_dg_voice_flap') }}</div>
              <div class="text-xs text-slate-700">✅ {{ $t('tel_dg_voice_media') }}</div>
            </div>
            <p class="mt-1 text-xs text-slate-500">{{ $t('tel_dg_voice_ref') }}</p>
          </div>

          <!-- 4. check -->
          <p class="font-medium">4. {{ $t('tel_guide_s4') }}</p>
          <p class="text-xs text-slate-500">⚠️ {{ $t('tel_dg_note') }}</p>
          </template>
        </div>

        <!-- How inbound / callbacks work (collapsible explainer) -->
        <div class="mb-3">
          <button
            type="button"
            class="text-xs font-medium text-indigo-700 hover:underline"
            @click="showInboundFlow = !showInboundFlow"
          >
            {{ showInboundFlow ? '▼' : '▶' }} {{ $t('tel_callflow_title') }}
          </button>
        </div>
        <div v-if="showInboundFlow" class="mb-4 p-3 rounded-lg bg-indigo-50 border border-indigo-100 text-sm text-slate-700 space-y-2.5">
          <p class="text-xs text-slate-500">{{ $t('tel_flow_intro') }}</p>

          <ol class="space-y-2">
            <li class="flex gap-2">
              <span class="flex-shrink-0 flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-white text-[11px] font-semibold">1</span>
              <span>{{ $t('tel_flow_s1') }}</span>
            </li>
            <li class="flex gap-2">
              <span class="flex-shrink-0 flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-white text-[11px] font-semibold">2</span>
              <span>{{ $t('tel_flow_s2') }}</span>
            </li>
            <li class="flex gap-2">
              <span class="flex-shrink-0 flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-white text-[11px] font-semibold">3</span>
              <span>{{ $t('tel_flow_s3') }}</span>
            </li>
            <li class="flex gap-2">
              <span class="flex-shrink-0 flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-white text-[11px] font-semibold">4</span>
              <span>{{ $t('tel_flow_s4') }}</span>
            </li>
            <li class="flex gap-2">
              <span class="flex-shrink-0 flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-white text-[11px] font-semibold">5</span>
              <span>{{ $t('tel_flow_s5') }}</span>
            </li>
            <li class="flex gap-2">
              <span class="flex-shrink-0 flex h-5 w-5 items-center justify-center rounded-full bg-indigo-600 text-white text-[11px] font-semibold">6</span>
              <span>{{ $t('tel_flow_s6') }}</span>
            </li>
          </ol>

          <div class="pt-1 border-t border-indigo-100 space-y-1">
            <p class="text-xs font-medium text-slate-600">{{ $t('tel_flow_notes_title') }}</p>
            <p class="text-xs text-slate-500 flex gap-1.5"><span>•</span> {{ $t('tel_flow_note_busy') }}</p>
            <p class="text-xs text-slate-500 flex gap-1.5"><span>•</span> {{ $t('tel_flow_note_unknown') }}</p>
            <p class="text-xs text-slate-500 flex gap-1.5"><span>•</span> {{ $t('tel_flow_note_avail') }}</p>
          </div>
        </div>

        <!-- Inbound / callback setup guide (collapsible) -->
        <div class="mb-3">
          <button
            type="button"
            class="text-xs font-medium text-emerald-700 hover:underline"
            @click="showInboundGuide = !showInboundGuide"
          >
            {{ showInboundGuide ? '▼' : '▶' }} {{ $t('tel_inbound_guide_title') }}
          </button>
        </div>
        <div v-if="showInboundGuide" class="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-100 text-sm text-slate-700 space-y-3">
          <p class="text-xs text-slate-500">{{ $t('tel_inbound_guide_intro') }}</p>

          <!-- ── Dinstar inbound steps ── -->
          <template v-if="!isDongleGuide && guideBrand === 'dinstar'">
            <!-- 1. Dinstar hotline -->
            <div>
              <p class="font-medium">1. {{ $t('tel_inbound_g1_title') }}</p>
              <div class="mt-1 font-mono text-xs bg-white rounded border p-2 space-y-0.5">
                <div>Port Config → To VOIP Hotline: <b>9000</b></div>
                <div>Authenticate Password = &lt;{{ $t('tel_guide_same_secret') }}&gt;</div>
              </div>
            </div>

            <!-- 2. Dinstar Tel→IP -->
            <div>
              <p class="font-medium">2. {{ $t('tel_inbound_g2_title') }}</p>
              <div class="mt-1 font-mono text-xs bg-white rounded border p-2 space-y-0.5">
                <div>Routing → Tel→IP: Port Group → SIP Server</div>
                <div>Port Group → Select Mode: <b>Cyclic Ascending</b></div>
              </div>
            </div>
          </template>

          <!-- ── OpenVox VoxStack inbound steps ── -->
          <template v-else-if="!isDongleGuide">
            <!-- 1. OpenVox module group (spread across SIMs) -->
            <div>
              <p class="font-medium">1. {{ $t('tel_inbound_ov1_title') }}</p>
              <div class="mt-1 font-mono text-xs bg-white rounded border p-2 space-y-0.5">
                <div>ROUTING → Groups → <b>New Group</b> · Type: <b>MODULE</b></div>
                <div>Members: <b>All</b> (the lte/gsm ports) &nbsp;·&nbsp; Policy: <b>Ascending</b></div>
              </div>
            </div>

            <!-- 2. OpenVox Mobile→VoIP routing rule with fixed callee (hotline) -->
            <div>
              <p class="font-medium">2. {{ $t('tel_inbound_ov2_title') }}</p>
              <div class="mt-1 font-mono text-xs bg-white rounded border p-2 space-y-0.5">
                <div>ROUTING → Call Routing Rules → <b>New Call Routing Rule</b></div>
                <div>Call Comes in From: <b>MODULE group</b></div>
                <div>Send Call Through: <b>SIP endpoint</b> ({{ suggestedTrunk }})</div>
                <div>Advance Routing Rule → Forward Number: <b>9000</b></div>
                <div class="pt-1 text-slate-500">+ outbound rule: From <b>SIP endpoint</b> → Through <b>MODULE group</b></div>
              </div>
              <p class="mt-1 text-xs text-slate-500">{{ $t('tel_inbound_ov2_note') }}</p>
            </div>
          </template>

          <!-- 3. FreePBX trunk → CRM context (GoIP only) -->
          <div v-if="!isDongleGuide">
            <p class="font-medium">3. {{ $t('tel_inbound_g3_title') }}</p>
            <div class="mt-1 font-mono text-xs bg-white rounded border p-2 space-y-0.5">
              <div>Trunks → <b>{{ suggestedTrunk }}</b> → pjsip Settings → Context: <b>crm-inbound</b></div>
            </div>
            <p class="mt-1 text-xs text-slate-500">{{ $t('tel_inbound_g3_note') }}</p>
          </div>

          <!-- Dongle inbound: handled by the mini-PC dialplan, no GoIP web-UI. -->
          <p v-if="isDongleGuide" class="text-xs text-slate-600">{{ $t('tel_dg_inbound') }}</p>

          <p class="text-xs text-emerald-700">✅ {{ $t('tel_inbound_g_how') }}</p>
          <p class="text-xs text-slate-500">🩺 {{ $t('tel_inbound_g_verify') }}</p>

          <!-- Troubleshooting & SIM/port maintenance (GoIP appliance concepts) -->
          <div v-if="!isDongleGuide" class="mt-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-1.5">
            <div class="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
              <Icon icon="mdi:wrench-outline" /> {{ $t('tel_ts_title') }}
            </div>
            <p class="text-xs text-slate-600 flex gap-1.5">
              <span>🔴</span><span><b>{{ $t('tel_ts_undetected_t') }}</b> — {{ $t('tel_ts_undetected_d') }}</span>
            </p>
            <p class="text-xs text-slate-600 flex gap-1.5">
              <span>⏭️</span><span><b>{{ $t('tel_ts_skipport_t') }}</b> — {{ $t('tel_ts_skipport_d') }}</span>
            </p>
            <p class="text-xs text-slate-600 flex gap-1.5">
              <span>💳</span><span><b>{{ $t('tel_ts_balance_t') }}</b> — {{ $t('tel_ts_balance_d') }}</span>
            </p>
            <p class="text-xs text-slate-600 flex gap-1.5">
              <span>📞</span><span><b>{{ $t('tel_ts_flow_t') }}</b> — {{ $t('tel_ts_flow_d') }}</span>
            </p>
          </div>

          <!-- Inbound health check (diagnostic) -->
          <div class="mt-2 rounded-lg border border-emerald-200 bg-white p-3">
            <div class="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
              <Icon icon="mdi:heart-pulse" class="text-emerald-600" />
              {{ $t('tel_intest_title') }}
            </div>
            <p class="text-xs text-slate-500 mb-2">{{ $t('tel_intest_intro') }}</p>

            <div class="flex flex-wrap items-end gap-2">
              <div>
                <label class="lbl">{{ $t('tel_intest_caller') }}</label>
                <input v-model="inboundTestCaller" class="inp" placeholder="+2126..." dir="ltr" />
              </div>
              <button
                type="button"
                :disabled="inboundTesting"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                @click="runInboundTest"
              >
                <Icon :icon="inboundTesting ? 'eos-icons:three-dots-loading' : 'mdi:play'" class="text-base" />
                {{ $t('tel_intest_run') }}
              </button>
            </div>

            <!-- Result -->
            <div v-if="inboundTestResult" class="mt-3 text-xs">
              <div v-if="!inboundTestResult.ok" class="text-red-600">
                {{ inboundTestResult.error || $t('tel_intest_failed') }}
              </div>
              <div v-else class="space-y-2">
                <!-- warnings -->
                <div v-if="(inboundTestResult.warnings || []).length" class="space-y-1">
                  <div
                    v-for="w in inboundTestResult.warnings"
                    :key="w"
                    class="flex items-start gap-1.5 rounded bg-amber-50 border border-amber-200 px-2 py-1 text-amber-700"
                  >
                    <Icon icon="mdi:alert-outline" class="text-sm flex-shrink-0 mt-0.5" />
                    <span>{{ $t('tel_intest_w_' + w) }}</span>
                  </div>
                </div>

                <!-- header: warehouse / country -->
                <div class="flex flex-wrap gap-x-4 gap-y-1 rounded bg-slate-50 border p-2">
                  <div>{{ $t('tel_intest_warehouse') }}: <b>{{ inboundTestResult.warehouse || '—' }}</b></div>
                  <div>{{ $t('tel_intest_country') }}: <b>{{ inboundTestResult.country || '—' }}</b></div>
                </div>

                <!-- per-GoIP registration (handles multiple GoIPs) -->
                <div>
                  <div class="text-[11px] font-medium text-slate-500 mb-1">{{ $t('tel_intest_gateways_h') }}</div>
                  <div v-if="(inboundTestResult.gateways || []).length" class="space-y-1">
                    <div
                      v-for="g in inboundTestResult.gateways"
                      :key="g.trunk"
                      class="flex items-center justify-between gap-2 rounded border px-2 py-1"
                      :class="g.registered === false ? 'bg-red-50 border-red-200' : (g.registered ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200')"
                    >
                      <span class="font-medium text-slate-700 truncate">
                        {{ g.name }} <span class="text-slate-400 font-mono">({{ g.trunk }})</span>
                      </span>
                      <span
                        class="inline-flex items-center gap-1 flex-shrink-0"
                        :class="g.registered === false ? 'text-red-600' : (g.registered ? 'text-emerald-700' : 'text-slate-400')"
                      >
                        <Icon :icon="g.registered === false ? 'mdi:close-circle' : (g.registered ? 'mdi:check-circle' : 'mdi:help-circle-outline')" class="text-sm" />
                        {{ g.registered === false ? $t('tel_intest_gw_down') : (g.registered ? $t('tel_intest_gw_up') : $t('tel_intest_gw_unknown')) }}
                      </span>
                    </div>
                  </div>
                  <div v-else class="text-slate-400">{{ $t('tel_no_gateway') }}</div>
                </div>

                <!-- shared agents (whole warehouse) -->
                <div>
                  <div class="text-[11px] font-medium text-slate-500 mb-1 flex items-center gap-1">
                    {{ $t('tel_intest_agents_h') }}
                    <b :class="inboundTestResult.onlineCount ? 'text-emerald-700' : 'text-red-600'">
                      {{ inboundTestResult.onlineCount }}/{{ inboundTestResult.totalAgents }}
                    </b>
                    <span
                      v-if="inboundTestResult.sticky"
                      class="ml-auto text-[#5743BE]"
                    >{{ $t('tel_intest_sticky') }}: {{ inboundTestResult.sticky }}</span>
                  </div>
                  <div v-if="(inboundTestResult.agents || []).length" class="flex flex-wrap gap-1.5">
                    <span
                      v-for="a in inboundTestResult.agents"
                      :key="a.ext"
                      class="inline-flex items-center gap-1 rounded-full border px-2 py-0.5"
                      :class="a.online ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-400'"
                    >
                      <span class="w-1.5 h-1.5 rounded-full" :class="a.online ? 'bg-emerald-500' : 'bg-slate-300'"></span>
                      {{ a.name || a.ext }} ({{ a.ext }})
                    </span>
                  </div>
                  <p class="mt-1 text-[11px] text-slate-400">{{ $t('tel_intest_agents_note') }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!form.gateways.length" class="text-sm text-gray-400">{{ $t('tel_no_gateway') }}</div>

        <div v-for="(g, gi) in form.gateways" :key="gi" class="border rounded-lg p-3 mb-3 bg-gray-50">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label class="lbl">{{ $t('tel_name') }}</label>
              <input v-model="g.name" class="inp" placeholder="GoIP-1" />
            </div>
            <div>
              <label class="lbl">{{ $t('tel_type') }}</label>
              <select v-model="g.provider" class="inp" @change="onProviderChange(g)">
                <option value="goip">GoIP</option>
                <option value="dongle">{{ $t('tel_type_dongle') }}</option>
                <option value="sip_trunk">SIP Trunk</option>
              </select>
            </div>
            <div>
              <label class="lbl">{{ $t('tel_brand_label') }}</label>
              <!-- Brand options depend on the type: a dongle box is Huawei;
                   GoIP boxes are Dinstar/OpenVox. Keeps invalid pairs out. -->
              <select v-model="g.brand" class="inp">
                <template v-if="g.provider === 'dongle'">
                  <option value="huawei">Huawei</option>
                  <option value="other">{{ $t('tel_brand_other') }}</option>
                </template>
                <template v-else>
                  <option value="dinstar">Dinstar</option>
                  <option value="openvox">OpenVox</option>
                  <option value="other">{{ $t('tel_brand_other') }}</option>
                </template>
              </select>
            </div>
            <div>
              <label class="lbl">{{ $t('tel_model') }}</label>
              <input
                v-model="g.model"
                class="inp"
                :placeholder="g.provider === 'dongle' ? 'E173 / E153 / E1750' : 'TG400 / VS-GW1202'"
              />
            </div>
            <div>
              <!-- For a dongle gateway, 'channels' = number of dongles (SIMs). -->
              <label class="lbl">{{ g.provider === 'dongle' ? $t('tel_dongles_count') : $t('tel_channels') }}</label>
              <input v-model.number="g.channels" type="number" class="inp" />
            </div>
            <div>
              <label class="lbl">{{ $t('tel_trunk') }}</label>
              <input
                v-model="g.trunkName"
                class="inp"
                :class="{ 'border-red-400 ring-1 ring-red-300': trunkDupIndexes.includes(gi) }"
                :placeholder="g.provider === 'dongle' ? 'g0' : 'dinstar-goip'"
              />
              <p v-if="trunkDupIndexes.includes(gi)" class="mt-1 text-xs text-red-600">
                ⚠️ {{ $t('tel_gw_dup_here') }}
              </p>
              <!-- Dongle box: the "trunk" is the chan_dongle group the outbound
                   dialplan uses (Dongle/g0). Standalone mini-PC → 'g0'; when the
                   mini-PC is a SIP trunk on a hub PBX → the trunk name there. -->
              <p v-if="g.provider === 'dongle'" class="mt-1 text-[11px] text-slate-500">
                {{ $t('tel_dongle_trunk_hint') }}
              </p>
            </div>

            <!-- Per-warehouse mini-PC host (dongle gateways). Each warehouse has
                 its OWN dongle box; the Check/Scan/Reset actions target THIS IP,
                 never the global PBX host, so the boxes never get mixed up. Blank
                 → falls back to the global PBX host (single-box setups). -->
            <div v-if="g.provider === 'dongle'" class="sm:col-span-2">
              <label class="lbl">{{ $t('tel_dg_host') }}</label>
              <input
                v-model="g.host"
                class="inp"
                placeholder="192.168.1.10"
              />
              <p class="mt-1 text-[11px] text-slate-500">{{ $t('tel_dg_host_hint') }}</p>

              <!-- ── Onboard a NEW box (one-time bootstrap command) ──
                   A fresh box needs 5 foundations the CRM can't push over HTTPS
                   (crm-box.conf, crm-ext.php, sudoers, sox, dialplan) BEFORE the
                   buttons below work. The CRM never SSHes the box (no stored creds,
                   no secret leaves here) — it just BUILDS the ready-to-paste
                   onboard-box.sh command from the IP + options; you run it once in
                   your terminal. After it: Create trunk + Scan & Configure. -->
              <div class="mt-2">
                <button type="button"
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                  @click="onboardOpen === gi ? (onboardOpen = null) : (onboardOpen = gi)"
                  :title="$t('tel_onboard_hint')">
                  <Icon icon="mdi:rocket-launch-outline" class="text-base" />
                  {{ $t('tel_onboard_btn') }}
                </button>

                <div v-if="onboardOpen === gi" class="mt-2 p-3 rounded-lg border border-slate-200 bg-slate-50 space-y-2">
                  <p class="text-[11px] text-slate-500">{{ $t('tel_onboard_desc') }}</p>
                  <div class="grid grid-cols-2 gap-2">
                    <div>
                      <label class="text-[11px] text-slate-500">{{ $t('tel_onboard_user') }}</label>
                      <input v-model="onboardUser" class="inp py-1 text-xs" placeholder="root" />
                    </div>
                    <div>
                      <label class="text-[11px] text-slate-500">{{ $t('tel_onboard_key') }}</label>
                      <input v-model="onboardKey" class="inp py-1 text-xs" placeholder="~/.ssh/id_new" />
                    </div>
                  </div>
                  <label class="inline-flex items-center gap-1 text-[11px] text-slate-600">
                    <input type="checkbox" v-model="onboardSudo" /> {{ $t('tel_onboard_sudo') }}
                  </label>
                  <label class="inline-flex items-center gap-1 text-[11px] text-slate-600 block">
                    <input type="checkbox" v-model="onboardWithDongle" /> {{ $t('tel_onboard_with_dongle') }}
                  </label>
                  <!-- The ready command. The secret is NOT embedded — it's a shell
                       placeholder the operator fills from telephonyConfig, so no
                       secret ever appears in the browser/DOM. -->
                  <div class="relative">
                    <pre class="text-[11px] bg-slate-900 text-slate-100 rounded-lg p-2 overflow-x-auto whitespace-pre-wrap break-all">{{ onboardCommand(g) }}</pre>
                    <button type="button"
                      class="absolute top-1.5 right-1.5 px-2 py-0.5 rounded text-[10px] font-medium bg-slate-700 text-white hover:bg-slate-600"
                      @click="copyOnboardCommand(g)">
                      {{ onboardCopied ? $t('tel_onboard_copied') : $t('tel_onboard_copy') }}
                    </button>
                  </div>
                  <p class="text-[11px] text-amber-600">{{ $t('tel_onboard_secret_note') }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="flex items-center justify-between mt-2">
            <label class="inline-flex items-center gap-1 text-sm">
              <input type="checkbox" v-model="g.active" /> {{ $t('tel_gateway_active') }}
            </label>
            <div class="flex items-center gap-3">
              <!-- Trunk registration status -->
              <span
                v-if="gatewayStatus[gi]"
                class="text-xs px-2 py-0.5 rounded-full"
                :class="gatewayStatus[gi].online
                  ? 'bg-green-100 text-green-700'
                  : (gatewayStatus[gi].exists ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700')"
              >
                {{ gatewayStatus[gi].online ? '✓ ' + $t('tel_gw_online')
                   : (gatewayStatus[gi].exists ? $t('tel_gw_registered_off') : '✗ ' + $t('tel_gw_not_found')) }}
              </span>
              <button
                @click="checkGatewayConn(g, gi)"
                :disabled="checkingGateway === gi"
                class="text-xs text-blue-600 hover:underline disabled:opacity-50"
              >
                {{ checkingGateway === gi ? $t('tel_testing') : $t('tel_gw_check') }}
              </button>
              <button @click="removeGateway(gi)" class="text-red-500 text-sm">{{ $t('tel_delete') }}</button>
            </div>
          </div>

          <!-- ── Auto-trunk creation (dongle gateways only) ──
               Create THIS warehouse's PJSIP trunk automatically: the backend writes
               the VPS hub endpoint AND the mini-PC registration + reloads both boxes,
               so no manual pjsip_custom.conf edit / SSH. Needs a trunk name + the
               mini-PC host filled in above (save the gateway first). -->
          <div v-if="g.provider === 'dongle'" class="mt-3 border-t pt-3">
            <div class="flex items-center justify-between">
              <span class="text-xs font-medium text-slate-600">{{ $t('tel_dg_trunk_title') }}</span>
              <div class="flex flex-wrap items-center gap-2">
                <!-- Read-only: is the trunk connected + working on both sides? -->
                <button
                  type="button"
                  @click="checkTrunk(gi)"
                  :disabled="checkingTrunk === gi || creatingTrunk === gi || !g.trunkName || !g.host"
                  class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-violet-300 bg-white text-violet-700 shadow-sm hover:bg-violet-50 active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  :title="$t('tel_dg_trunk_check_hint')"
                >
                  <span>🔎</span>
                  {{ checkingTrunk === gi ? $t('tel_testing') : $t('tel_dg_trunk_check_btn') }}
                </button>
                <button
                  type="button"
                  @click="createTrunk(gi)"
                  :disabled="creatingTrunk === gi || checkingTrunk === gi || !g.trunkName || !g.host"
                  class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border violet-button border-violet-600 bg-violet-600 text-white shadow-sm hover:bg-violet-700 hover:border-violet-700 active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  :title="$t('tel_dg_trunk_hint2')"
                >
                  <span>🔗</span>
                  {{ creatingTrunk === gi ? $t('tel_dg_trunk_running') : $t('tel_dg_trunk_btn') }}
                </button>
              </div>
            </div>
            <p class="mt-1 text-[11px] text-slate-500">{{ $t('tel_dg_trunk_intro') }}</p>
            <!-- result — unified health card -->
            <TelHealthCard v-if="trunkResult[gi]" class="mt-2" :panel="_healthCard(
              trunkResult[gi],
              trunkResult[gi].mode === 'check' ? $t('tel_dg_trunk_healthy') : $t('tel_dg_trunk_ok'),
              trunkResult[gi].mode === 'check' ? $t('tel_dg_trunk_issues') : $t('tel_dg_trunk_partial')
            )" />
          </div>

          <!-- ── Dongle health panel (dongle gateways only) ── -->
          <div v-if="g.provider === 'dongle'" class="mt-3 border-t pt-3">
            <div class="flex items-center justify-between">
              <span class="text-xs font-medium text-slate-600">
                {{ $t('tel_dg_check_title') }}
                <span v-if="g.host" class="ml-1 font-normal text-slate-400">— {{ g.host }}</span>
              </span>
              <div class="flex flex-wrap items-center gap-2">
                <!-- Full setup health of THIS box (chan_dongle, sudoers, Voice,
                     trunk, power, network) with clear error messages — no SSH. -->
                <button
                  type="button"
                  @click="checkDongleHost(gi)"
                  :disabled="checkingHost === gi || autoConfiguring === gi || checkingDongles === gi"
                  class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg sky-button border border-sky-600 bg-sky-600 text-white shadow-sm hover:bg-sky-700 hover:border-sky-700 active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  :title="$t('tel_dg_host_check_hint')"
                >
                  <span>🩺</span>
                  {{ checkingHost === gi ? $t('tel_testing') : $t('tel_dg_host_check_btn') }}
                </button>
                <!-- Auto-detect + configure new/moved dongles (modeswitch, udev,
                     dongle.conf, reload) straight from the UI — no SSH. -->
                <button
                  type="button"
                  @click="autoConfigDongles(gi)"
                  :disabled="autoConfiguring === gi || checkingDongles === gi"
                  class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-emerald-600 bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 hover:border-emerald-700 active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  :title="$t('tel_dg_scan_hint')"
                >
                  <span>⚙️</span>
                  {{ autoConfiguring === gi ? $t('tel_dg_scan_running') : $t('tel_dg_scan_btn') }}
                </button>
                <button
                  type="button"
                  @click="checkDongles(gi)"
                  :disabled="checkingDongles === gi || autoConfiguring === gi"
                  class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-indigo-300 bg-white text-indigo-700 shadow-sm hover:bg-indigo-50 active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  :title="$t('tel_dg_check_hint')"
                >
                  <span>📋</span>
                  {{ checkingDongles === gi ? $t('tel_testing') : $t('tel_dg_check_btn') }}
                </button>
                <!-- Re-apply the chan_dongle voice patch after a fork recompile
                     wiped it (calls connect but are silent). Runs the patch
                     script + rebuild + reload on the box. -->
                <button
                  type="button"
                  @click="repatchDongles(gi)"
                  :disabled="repatching === gi || checkingDongles === gi || autoConfiguring === gi"
                  :title="$t('tel_dg_repatch_hint')"
                  class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg rose-button border border-rose-300 bg-white text-rose-700 shadow-sm hover:bg-rose-50 active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>🔧</span>
                  {{ repatching === gi ? $t('tel_dg_repatch_running') : $t('tel_dg_repatch_btn') }}
                </button>
                <!-- Live auto-refresh of the dongle list (~15s) so a dongle that
                     drops/recovers is visible without re-clicking. -->
                <label class="inline-flex items-center gap-1 text-[11px] text-slate-500 cursor-pointer ml-1">
                  <input type="checkbox" :checked="autoRefreshGi === gi" @change="toggleAutoRefresh(gi)" />
                  {{ $t('tel_dg_autorefresh') }}
                </label>
              </div>
            </div>

            <!-- mini-PC health report (Check mini-PC) — unified health card -->
            <TelHealthCard v-if="hostHealth[gi]" class="mt-2" :panel="_healthCard(
              hostHealth[gi], $t('tel_dg_host_ok'), $t('tel_dg_host_issues', { n: (hostHealth[gi] && hostHealth[gi].errors) || 0 })
            )" />

            <!-- results -->
            <div v-if="dongleStatus[gi]" class="mt-2">
              <p v-if="dongleStatus[gi].error" class="text-xs text-red-600">
                {{ dongleStatus[gi].error }}
              </p>
              <!-- RECONFIGURING: a stick was plugged/unplugged (or the driver is
                   reloading) and chan_dongle is briefly down. Show a spinner +
                   "please wait" instead of a false "no dongles" alarm; checkDongles
                   auto-re-polls every 5s until it clears. -->
              <div v-else-if="dongleStatus[gi].reloading" class="text-xs text-sky-700 flex items-center gap-2">
                <svg class="animate-spin h-4 w-4 text-sky-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                <span>{{ dongleStatus[gi].message || $t('tel_dg_reloading') }}</span>
              </div>
              <div v-else-if="!(dongleStatus[gi].dongles || []).length" class="text-xs text-amber-600">
                {{ $t('tel_dg_check_none') }}
              </div>
              <table v-else class="w-full text-xs mt-1">
                <thead>
                  <tr class="text-slate-400 text-left">
                    <th class="font-medium pb-1">{{ $t('tel_dg_col_device') }}</th>
                    <th class="font-medium pb-1" :title="$t('tel_dg_col_model_hint')">{{ $t('tel_dg_col_model') }}</th>
                    <th class="font-medium pb-1">{{ $t('tel_dg_col_status') }}</th>
                    <th class="font-medium pb-1">Voice</th>
                    <th class="font-medium pb-1">RSSI</th>
                    <th class="font-medium pb-1">{{ $t('tel_dg_col_provider') }}</th>
                    <th class="font-medium pb-1" :title="$t('tel_dg_col_tty_hint')">tty</th>
                    <th class="font-medium pb-1"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(d, di) in dongleStatus[gi].dongles" :key="di" class="border-t border-slate-100">
                    <td class="py-1 font-mono">
                      {{ d.device }}
                      <span v-if="d.number" class="block text-[10px] text-slate-400">{{ d.number }}</span>
                    </td>
                    <!-- Hardware identity: which physical stick this is. Firmware +
                         IMEI on hover so a faulty/mismatched stick is identifiable. -->
                    <td class="py-1">
                      <span class="text-slate-600" :title="[d.firmware ? ('fw ' + d.firmware) : '', d.imei ? ('IMEI ' + d.imei) : ''].filter(Boolean).join(' · ')">
                        {{ d.model || '—' }}
                      </span>
                      <span v-if="d.firmware" class="block text-[10px] text-slate-400">{{ d.firmware }}</span>
                    </td>
                    <td class="py-1">
                      <span
                        class="px-1.5 py-0.5 rounded-full"
                        :class="d.stuck ? 'bg-red-100 text-red-700'
                          : (/free/i.test(d.status) ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700')"
                      >{{ d.status || '—' }}</span>
                      <!-- GSM not registered: dongle is Free but can't place calls. -->
                      <span
                        v-if="d.gsmReg && !/register/i.test(d.gsmReg)"
                        :title="$t('tel_dg_gsm_hint')"
                        class="ml-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700"
                      >📵 {{ d.gsmReg }}</span>
                      <!-- SIM with no credit: answers but bills 0s (operator "no
                           balance" intercept). Red = STRONG signal (≥3 zero-billed
                           calls) OR the dongle was AUTO-disabled for no credit — a
                           parked dongle makes no more calls so zeroBillCalls decays
                           out of the 6h window; without this, a disabled SIM would
                           show only "Re-enable" with no reason. Orange = the weaker
                           short-call heuristic. -->
                      <span
                        v-if="d.suspectSim || d.disabled"
                        :title="$t('tel_dg_suspect_hint') + (d.zeroBillCalls ? ' ('+d.zeroBillCalls+' × 0s)' : '')"
                        :class="(d.zeroBillCalls >= 3 || d.disabled) ? 'ml-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700' : 'ml-1 px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700'"
                      >{{ (d.zeroBillCalls >= 3 || d.disabled) ? ('💳 ' + $t('tel_dg_no_credit')) : ('⚠ ' + $t('tel_dg_suspect')) }}</span>
                    </td>
                    <td class="py-1">
                      <span :class="d.voice ? 'text-green-600' : 'text-red-500'">{{ d.voice ? '✓' : '✗' }}</span>
                    </td>
                    <td class="py-1">{{ d.rssi || '—' }}</td>
                    <td class="py-1 text-slate-500">{{ d.provider || '—' }}</td>
                    <!-- tty mapping: data = the AT-command port (must answer AT),
                         audio = the PCM voice port. Wrong mapping ⇒ silent/Not-connected. -->
                    <td class="py-1 font-mono text-[10px] text-slate-500 whitespace-nowrap">
                      <template v-if="d.dataTty">
                        <span :title="$t('tel_dg_tty_data')">D:{{ d.dataTty }}<span v-if="d.dataIface != null" class="text-slate-400">.{{ d.dataIface }}</span></span>
                        <span v-if="d.audioTty" :title="$t('tel_dg_tty_audio')"> · A:{{ d.audioTty }}<span v-if="d.audioIface != null" class="text-slate-400">.{{ d.audioIface }}</span></span>
                      </template>
                      <span v-else>—</span>
                    </td>
                    <td class="py-1 text-right whitespace-nowrap">
                      <button
                        v-if="d.stuck || !d.voice"
                        type="button"
                        @click="resetDongle(gi, d.device)"
                        :disabled="resettingDongle === d.device"
                        class="text-[11px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                      >
                        {{ resettingDongle === d.device ? '…' : $t('tel_dg_reset') }}
                      </button>
                      <!-- Park / re-enable ANY dongle by hand (not only a suspected
                           no-credit SIM): a parked SIM (group 999) is skipped by the
                           auto-dialer/predictive call group. Disabled → shows
                           "Re-enable"; every other dongle → "Disable". -->
                      <button
                        v-if="d.disabled"
                        type="button"
                        @click="toggleDongleEnabled(gi, d.device, true)"
                        :disabled="togglingDongle === d.device"
                        class="ml-1 text-[11px] px-1.5 py-0.5 rounded bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50"
                      >
                        {{ togglingDongle === d.device ? '…' : $t('tel_dg_reenable') }}
                      </button>
                      <button
                        v-else
                        type="button"
                        @click="toggleDongleEnabled(gi, d.device, false)"
                        :disabled="togglingDongle === d.device"
                        class="ml-1 text-[11px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
                        :title="$t('tel_dg_disable_hint')"
                      >
                        {{ togglingDongle === d.device ? '…' : $t('tel_dg_disable') }}
                      </button>
                      <!-- Real test call through THIS dongle: the manager dials a
                           number they control to hear if the SIM has credit (normal
                           ring) or not ("solde épuisé"). Ground-truth SIM check. -->
                      <button
                        type="button"
                        @click="openDongleTestCall(gi, d.device)"
                        :disabled="testingDongle === d.device"
                        class="ml-1 text-[11px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 hover:bg-sky-100 disabled:opacity-50"
                      >
                        {{ testingDongle === d.device ? '…' : ('📞 ' + $t('tel_dg_testcall')) }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p v-if="dongleStatus[gi].needsSave" class="mt-1 text-[11px] font-medium text-amber-700">
                ⚠️ {{ $t('tel_dg_needs_save') }}
              </p>
              <p class="mt-1 text-[11px] text-slate-400">{{ $t('tel_dg_check_hint') }}</p>
            </div>
          </div>
        </div>
      </div>

      </div><!-- /TAB gateways -->

      </template>

      <!-- Save (always visible under the tabs) -->
      <div class="flex justify-end">
        <button @click="save" :disabled="saving" class="btn-primary">
          {{ $t('save') }}
        </button>
      </div>

      <!-- Dongle test-call modal — MUST live here, at the always-rendered root
           (outside every `v-show` tab pane). It is opened from the Passerelles tab
           (📞 Test call) but a modal inside a display:none tab pane opens invisibly
           (it fired, no error, but was hidden — the bug fixed 2026-09-04 after the
           tab redesign moved it into the hidden 'ext' pane). Choose the dongle's
           test target: the manager hears the call on their own softphone. -->
      <vue-modal-2
        name="DongleTestModal"
        :headerOptions="{ title: $t('tel_dg_testcall') }"
        modalSize="md"
        :noFooter="true"
        @on-close="$vm2.close('DongleTestModal')"
      >
        <div class="bg-white rounded-b-lg px-5 py-4 space-y-4">
          <p class="text-xs text-slate-500">
            📞 {{ $t('tel_dg_testcall_intro') }}
            <b v-if="dtc.device" class="text-sky-700 font-mono">{{ dtc.device }}</b>
          </p>

          <!-- The MANAGER hears the test on their OWN softphone. If they have an
               extension → just say so; if not → a button to create it right here. -->
          <div v-if="managerHasExtension" class="flex items-center gap-2 text-[13px] text-slate-600">
            <Icon icon="mdi:headphones" class="text-base text-sky-600" />
            {{ $t('tel_dg_testcall_youhear') }}
          </div>
          <div v-else class="flex items-center gap-2 text-[13px]">
            <span class="text-amber-600">⚠ {{ $t('tel_dg_testcall_noext') }}</span>
            <button type="button"
              :disabled="dtcGenerating"
              class="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
              @click="generateMyExtension">
              <Icon :icon="dtcGenerating ? 'eos-icons:three-dots-loading' : 'mdi:phone-plus'" class="text-sm" />
              {{ $t('tel_dg_testcall_genmine') }}
            </button>
          </div>

          <!-- Country -->
          <div v-if="dongleTestCountries.length > 1">
            <label class="block text-xs font-medium text-slate-600 mb-1">{{ $t('tel_dg_testcall_country') }}</label>
            <div class="flex gap-2">
              <button v-for="c in dongleTestCountries" :key="c" type="button"
                @click="dtc.country = c"
                class="px-3 py-1.5 rounded-lg text-sm border transition"
                :class="dtc.country === c ? 'border-sky-500 bg-sky-50 text-sky-700 font-medium' : 'border-slate-200 text-slate-600 hover:border-slate-300'">
                {{ flagEmoji(c) }} {{ c }}
              </button>
            </div>
          </div>

          <!-- Number -->
          <div>
            <label class="block text-xs font-medium text-slate-600 mb-1">{{ $t('tel_dg_testcall_number') }}</label>
            <input v-model="dtc.number" type="tel" inputmode="tel"
              :placeholder="$t('tel_dg_testcall_number_ph')"
              class="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sky-300 focus:border-sky-400 outline-none"
              @keyup.enter="submitDongleTest" />
          </div>

          <!-- Actions. NOTE inline style color: the Tailwind text/bg color utilities
               (text-slate-600, text-white bg-sky-600) aren't emitted in this purged
               build, so the button text was invisible — set the colors explicitly. -->
          <div class="flex items-center justify-end gap-2 pt-1">
            <button type="button" class="px-4 py-2 rounded-lg text-sm hover:bg-slate-100"
              style="color:#475569"
              @click="$vm2.close('DongleTestModal')">{{ $t('cancel') || 'Annuler' }}</button>
            <!-- Disabled until: a number is typed AND the manager has an extension
                 (no extension = no softphone to hear on → generate it first). -->
            <button type="button"
              :disabled="dtcSubmitting || !dtc.number || !managerHasExtension"
              :title="!managerHasExtension ? $t('tel_dg_testcall_noext') : ''"
              class="sky-button inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              @click="submitDongleTest">
              <Icon :icon="dtcSubmitting ? 'eos-icons:three-dots-loading' : 'mdi:phone-outgoing'" class="text-base" />
              {{ $t('tel_dg_testcall_go') }}
            </button>
          </div>
        </div>
      </vue-modal-2>
    </div>
  </div>
</template>

<script>
import axios from "../../common/axios";
import bus from "../../common/eventBus";
import softphoneState from "../../common/softphoneState";
import reduxStore from "../../store";
import TelephonyGuide from "../../components/Telephony/TelephonyGuide.vue";
import TelHealthCard from "../../components/Telephony/HealthCard.vue";
import AiSwitch from "../../components/ui/AiSwitch.vue";

// ARI password + webhook secret are NOT in this form — they live in
// server config (config/*.json → telephony.*), never sent from the UI.
const emptyForm = () => ({
  enabled: false,
  pbx: { host: "", ariUser: "", ariPort: 8088, context: "from-internal" },
  gateways: [],
  recording: { enabled: true, retentionDays: 90 },
  defaultCallerId: "",
  // Per-warehouse call mode: "manual" (agent clicks Call) or "auto" (auto-dialer
  // campaign). Defaults manual so nothing changes for existing warehouses.
  callMode: "manual",
  // ringsBeforeNoAnswer is kept as an internal default (drives the browser
  // outcome-poll timeout) but is NOT exposed in the UI — the REAL ring timeout is
  // the dialplan Dial(...,60) on the VPS, so a UI field here would mislead.
  autoDialer: { delayBetweenCalls: 3, ringsBeforeNoAnswer: 5, recheckEmptyMins: 2, waitForClientAnswer: false },
  // Predictive/queue config (used only when callMode === "predictive").
  predictiveDialer: { waitMessageUrl: "", maxWaitSecs: 300, dialAheadPerAgent: 1, delayBetweenCalls: 3 },
});

export default {
  name: "TelephonySettings",
  components: { TelephonyGuide, TelHealthCard, AiSwitch },
  // `warehouse` is the warehouse selected in the /settings header
  // (same object the other tabs use via `idWharhouse`).
  props: {
    warehouse: { type: Object, default: null },
  },
  data() {
    return {
      form: emptyForm(),
      // Section tabs — split the long settings page into scannable areas.
      activeTab: "mode",
      telTabs: [
        { id: "mode", label: "tel_tab_mode", icon: "mdi:phone-dial" },
        { id: "ext", label: "tel_tab_ext", icon: "mdi:account-multiple-check" },
        { id: "pbx", label: "tel_tab_pbx", icon: "mdi:server-network" },
        { id: "gateways", label: "tel_tab_gateways", icon: "mdi:router-wireless" },
      ],
      // Guide renders only after the modal opens (so its autoplay timers don't run
      // while it's hidden), and is torn down on close.
      guideOpen: false,
      loading: false,
      saving: false,
      testing: false,
      testResult: null,
      // "Check VPS" — full health of the global VPS hub (ARI, trunk, agents, WSS).
      checkingVps: false,
      vpsHealth: null,
      syncingConfig: false,
      syncResult: null,
      // This install's identity (from the API, non-secret) — pre-fills the onboarding command.
      projectMeta: { projectId: "", scriptsApiUrl: "" },
      // Pushing the predictive wait message to the box(es) (per-project WAV + MOH).
      syncingWaitMsg: false,
      // "Onboard a new box" helper (builds the onboard-box.sh command, no SSH here).
      onboardOpen: null,     // gi whose onboard panel is open
      onboardUser: "root",
      onboardKey: "",
      onboardSudo: false,
      onboardWithDongle: false,
      onboardCopied: false,
      // Per-gateway FreePBX trunk registration check (keyed by index).
      gatewayStatus: {},
      checkingGateway: null,
      // Per-dongle-gateway health panel (keyed by gateway index).
      dongleStatus: {},
      checkingDongles: null,
      resettingDongle: null,
      togglingDongle: null, // dongle being disabled/re-enabled (no-credit park)
      testingDongle: null,  // dongle currently placing a manual test call
      dtc: { gi: null, device: "", ext: "", country: "", number: "" }, // dongle-test modal form
      dtcSubmitting: false,
      dtcGenerating: false, // creating the manager's own extension from the modal
      autoConfiguring: null,
      repatching: null, // gi being re-patched (chan_dongle voice patch)
      hostHealth: {}, // gi → mini-PC health report
      checkingHost: null,
      trunkResult: {}, // gi → auto-trunk creation/check result (checks[])
      creatingTrunk: null, // gi being provisioned
      checkingTrunk: null, // gi being checked
      autoRefreshGi: null, // which gateway index is live-refreshing (one at a time)
      autoRefreshTimer: null,
      showGuide: false,
      showFlow: false, // "How it works" call-flow explainer panel
      showPbxGuide: false,
      showInboundGuide: false,
      showInboundFlow: false,
      // Which GoIP brand the setup guides show steps for (Dinstar / OpenVox).
      guideBrand: "dinstar",
      // Inbound health-check diagnostic (inside the inbound guide).
      inboundTestCaller: "",
      inboundTesting: false,
      inboundTestResult: null,
      checkingExts: false,
      extResult: null,
      extActionId: null, // userId currently generating/toggling (button spinner)
      extTab: "with",    // which section tab is showing: "with" | "without"
      uploadingWaitMsg: false, // predictive wait-message upload in flight
      // Wait-message source picker: "default" = built-in French voice on the VPS
      // (crm-waitmsg-default.wav, used when no custom URL is set); "custom" = the
      // manager's own MP3. Initialized from the loaded settings; a watcher clears
      // the URL when switched back to "default".
      // The built-in French default wait voice (hosted on the CDN so the manager can
      // preview it here). The SAME audio is stored on the VPS as crm-waitmsg-default.wav
      // and is what actually plays to a waiting client when no custom MP3 is set.
      // Preview URL of the built-in default wait voice (empty → no preview; the PBX
      // still plays its own crm-waitmsg-default.wav when no custom MP3 is set).
      defaultWaitMsgUrl: "",
    };
  },
  computed: {
    warehouseId() {
      return this.warehouse && this.warehouse._id;
    },
    // Normalize whichever VPS-panel result is active (Test / Check VPS / Sync config)
    // into ONE shape { headerLevel, title, rows:[{level,text,detail}] } for the unified
    // card. Only one is ever set at a time (handlers call _clearVpsPanels first).
    healthPanel() {
      // Check VPS — the richest: a list of {level, message, detail} checks.
      if (this.vpsHealth) {
        if (this.vpsHealth.error) {
          return { headerLevel: "error", title: this.vpsHealth.error, rows: [] };
        }
        const rows = (this.vpsHealth.checks || []).map((c) => ({
          level: c.level || (c.ok ? "ok" : "error"),
          text: c.message,
          detail: c.detail || "",
        }));
        const hasErr = rows.some((r) => r.level === "error");
        const hasWarn = rows.some((r) => r.level === "warn");
        return {
          headerLevel: hasErr ? "error" : (hasWarn ? "warn" : "ok"),
          title: this.vpsHealth.healthy && !hasErr && !hasWarn
            ? this.$t("tel_vps_ok")
            : this.$t("tel_vps_issues", { n: this.vpsHealth.errors || rows.filter((r) => r.level === "error").length }),
          rows,
        };
      }
      // Sync config — per-box ok/error rows.
      if (this.syncResult) {
        const rows = (this.syncResult.results || []).map((r) => ({
          level: r.ok ? "ok" : "error",
          text: r.host || "—",
          detail: r.error || "",
        }));
        return {
          headerLevel: this.syncResult.synced ? "ok" : "warn",
          title: this.syncResult.synced ? this.$t("tel_sync_config_ok") : this.$t("tel_sync_config_partial"),
          rows,
        };
      }
      // Test connection — a single ok/error line.
      if (this.testResult) {
        return {
          headerLevel: this.testResult.ok ? "ok" : "error",
          title: this.testResult.ok
            ? (this.$t("tel_connected") + " " + (this.testResult.version || ""))
            : (this.testResult.error || "Connection failed"),
          rows: [],
        };
      }
      return null;
    },
    // True when the active wait message IS the built-in default voice (so the
    // caption can say "default" vs "your uploaded message").
    isDefaultWaitMsg() {
      const url = this.form && this.form.predictiveDialer && this.form.predictiveDialer.waitMessageUrl;
      return !!url && url === this.defaultWaitMsgUrl;
    },
    // Indexes of gateways whose trunkName duplicates another row IN THIS
    // warehouse — highlighted red inline. (Cross-warehouse duplicates can only
    // be checked server-side, since this page loads one warehouse at a time.)
    trunkDupIndexes() {
      const names = (this.form.gateways || []).map((g) =>
        (g.trunkName || "").trim().toLowerCase()
      );
      const dups = [];
      names.forEach((n, i) => {
        if (n && names.indexOf(n) !== i) {
          dups.push(i, names.indexOf(n));
        }
      });
      return [...new Set(dups)];
    },
    warehouseName() {
      return (this.warehouse && this.warehouse.name) || "";
    },
    // The logged-in user (the manager running the test). Used to offer a "generate
    // my own extension" shortcut in the test modal when they have none.
    currentUser() {
      return (reduxStore && reduxStore.state && reduxStore.state.user) || {};
    },
    managerHasExtension() {
      const t = this.currentUser && this.currentUser.telephony;
      return !!(t && t.extension);
    },
    // Agents (with an extension) to offer in the dongle-test modal — online ones
    // first (they can actually hear the call). Sourced from the last checkExtensions
    // result; the modal triggers a refresh on open so the list is current.
    dtcAgents() {
      const list = ((this.extResult && this.extResult.extensions) || [])
        .filter((e) => e && e.extension && e.exists !== false && e.activeInCrm);
      return list.slice().sort((a, b) => (b.online ? 1 : 0) - (a.online ? 1 : 0));
    },
    // Countries to offer for a dongle test call: this warehouse's own country PLUS
    // Morocco ("MA") — the dongles hold Moroccan SIMs, so a test number is usually
    // dialed in MA format even when the warehouse serves another country. Dedup +
    // keep order (warehouse country first if it exists, then MA).
    dongleTestCountries() {
      const list = [];
      const wc = this.warehouse && this.warehouse.country;
      if (wc) list.push(String(wc).toUpperCase());
      if (!list.includes("MA")) list.push("MA");
      return list;
    },
    // True when the guide is showing the GSM-dongle setup (vs a GoIP brand).
    isDongleGuide() {
      return this.guideBrand === "huawei";
    },
    // Suggested trunk/group name for this warehouse, shown in the setup guide so
    // the value is copy-ready. GoIP → a distinct PBX trunk per warehouse
    // ("goip-tchad"). Dongle → the chan_dongle group the outbound dialplan uses
    // ("g0" on a standalone mini-PC; the SIP trunk name if it's a trunk on a hub).
    suggestedTrunk() {
      if (this.isDongleGuide) return "g0";
      const slug = ((this.warehouse && this.warehouse.name) || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      return slug ? "goip-" + slug : "goip-<warehouse>";
    },
    // PBX host from the settings above (so the guide shows the real address).
    // The hub PBX is the office LAN IP (192.168.100.244), a dongle mini-PC
    // (192.168.100.254), or a future public VPS — whatever is set in pbx.host.
    pbxHostDisplay() {
      return (this.form && this.form.pbx && this.form.pbx.host) || "<PBX host>";
    },
  },
  watch: {
    warehouseId: {
      immediate: true,
      handler(id) {
        this._stopAutoRefresh(); // gateway indices change when the warehouse does
        if (id) this.loadSettings();
      },
    },
  },
  beforeDestroy() {
    // Stop the dongle live-refresh so the interval doesn't leak after leaving.
    this._stopAutoRefresh();
  },
  methods: {
    // Open the animated call-modes guide in a modal. Mount the guide only now so
    // its autoplay animation starts fresh each time (and stops when closed).
    openGuide() {
      this.guideOpen = true;
      this.$vm2.open("TelephonyGuideModal");
    },
    closeGuide() {
      this.$vm2.close("TelephonyGuideModal");
      this.guideOpen = false; // unmount the guide so its timers stop
    },
    async loadSettings() {
      if (!this.warehouseId) return;
      // Rebuilding form.gateways below invalidates any live refresh index — stop
      // it so a queued tick can't mistarget the fresh array (save/scan reload).
      this._stopAutoRefresh();
      this.loading = true;
      this.testResult = null;
      try {
        const res = await axios.get("telephony/getSettings?warehouseId=" + this.warehouseId);
        const ts = res.data && (res.data.telephony_settings || (res.data.content && res.data.content.telephony_settings));
        const meta = res.data && (res.data.meta || (res.data.content && res.data.content.meta));
        if (meta) this.projectMeta = { projectId: meta.projectId || "", scriptsApiUrl: meta.scriptsApiUrl || "" };
        this.form = Object.assign(emptyForm(), ts || {});
        if (!this.form.pbx) this.form.pbx = emptyForm().pbx;
        if (!this.form.recording) this.form.recording = emptyForm().recording;
        if (!Array.isArray(this.form.gateways)) this.form.gateways = [];
        // A warehouse saved BEFORE the predictive feature has no predictiveDialer
        // (or a null one from the backend); Object.assign would overwrite the default
        // with null and the predictive template (form.predictiveDialer.waitMessageUrl)
        // would crash → the whole Settings page renders empty. Restore the default.
        if (!this.form.predictiveDialer || typeof this.form.predictiveDialer !== "object") {
          this.form.predictiveDialer = emptyForm().predictiveDialer;
        } else {
          // Fill any missing sub-keys (partial object from an older save).
          this.form.predictiveDialer = Object.assign(emptyForm().predictiveDialer, this.form.predictiveDialer);
        }
        if (!this.form.callMode) this.form.callMode = "manual";
        // Default the guide tab to the first configured gateway: a dongle box
        // (provider dongle / brand huawei) → the Dongle guide; else its GoIP brand.
        const g0 = this.form.gateways.find((g) => g && (g.brand || g.provider));
        if (g0) {
          if (g0.provider === "dongle" || g0.brand === "huawei") this.guideBrand = "huawei";
          else if (g0.brand === "dinstar" || g0.brand === "openvox") this.guideBrand = g0.brand;
        }
      } catch (e) {
        this._toast("Failed to load settings: " + e.message, "danger");
      } finally {
        this.loading = false;
      }
    },
    addGateway() {
      // Seed the new row from whichever guide tab is active, so adding a gateway
      // right after picking "Dongle" starts as a dongle (not a GoIP).
      const dongle = this.guideBrand === "huawei";
      this.form.gateways.push({
        name: "",
        provider: dongle ? "dongle" : "goip",
        brand: dongle ? "huawei" : (this.guideBrand || "dinstar"),
        host: "", model: "",
        channels: dongle ? 3 : 1,
        trunkName: dongle ? "g0" : "",
        dongleDevices: [], sims: [], active: true,
      });
    },
    // Keep brand valid when the gateway type changes (a dongle is Huawei; a GoIP
    // is Dinstar/OpenVox) and pre-fill the dongle group name.
    onProviderChange(g) {
      if (g.provider === "dongle") {
        if (g.brand !== "huawei" && g.brand !== "other") g.brand = "huawei";
        if (!g.trunkName) g.trunkName = "g0";
      } else if (g.brand === "huawei") {
        g.brand = "dinstar";
      }
    },
    // Check whether this gateway's FreePBX trunk is registered/online.
    async checkGatewayConn(g, gi) {
      if (!g.trunkName) {
        this._toast(this.$t("tel_gw_no_trunk"), "danger");
        return;
      }
      this.checkingGateway = gi;
      this.$set(this.gatewayStatus, gi, null);
      try {
        const res = await axios.post("telephony/checkGateway", { trunkName: g.trunkName, warehouseId: this.warehouseId });
        const c = res.data && (res.data.content || res.data);
        this.$set(this.gatewayStatus, gi, { online: !!c.online, exists: !!c.exists });
      } catch (e) {
        this.$set(this.gatewayStatus, gi, { online: false, exists: false, error: true });
      } finally {
        this.checkingGateway = null;
      }
    },
    // Dongle health: read each dongle's live state (Free/stuck, Voice, RSSI,
    // Provider) from the mini-PC. A per-dongle Reset appears for stuck/no-voice
    // ones. Only for dongle gateways.
    // Full setup health of THIS warehouse's mini-PC (chan_dongle, sudoers,
    // dongles+Voice, trunk, power, network, version) with clear messages so a
    // new box is debuggable without SSH. Targets the gateway's own host.
    async checkDongleHost(gi) {
      this.checkingHost = gi;
      this.$set(this.hostHealth, gi, null);
      try {
        const gw = this.form.gateways[gi] || {};
        const res = await axios.post(
          "telephony/checkDongleHost",
          {
            warehouseId: this.warehouseId,
            trunkName: gw.trunkName,
            host: gw.host,
            channels: gw.channels,
          },
          { timeout: 40000 }
        );
        const c = res.data && (res.data.content || res.data);
        if (!c || c.ok === false) {
          this.$set(this.hostHealth, gi, {
            error: c && c.reason === "not_supported"
              ? this.$t("tel_dg_check_unsupported")
              : ((c && c.error) || this.$t("tel_dg_host_check_fail")),
          });
        } else {
          this.$set(this.hostHealth, gi, {
            healthy: !!c.healthy,
            errors: c.errors || 0,
            checks: c.checks || [],
          });
        }
      } catch (e) {
        this.$set(this.hostHealth, gi, { error: this.$t("tel_dg_host_check_fail") });
      } finally {
        this.checkingHost = null;
      }
    },
    // Auto-create this warehouse's PJSIP trunk: the backend writes BOTH the VPS
    // hub endpoint AND the mini-PC registration + reloads, so no manual
    // pjsip_custom.conf / SSH. Reuses the checks[] renderer (like checkDongleHost).
    // Requires a trunk name + mini-PC host filled in (button is disabled otherwise).
    async createTrunk(gi) {
      const gw = this.form.gateways[gi] || {};
      if (!gw.trunkName || !gw.host) return;
      // A brand-new trunk on a shared VPS is a live config write + reload on both
      // boxes — confirm so it's never an accidental click.
      if (!window.confirm(this.$t("tel_dg_trunk_confirm", { trunk: gw.trunkName }))) return;
      this.creatingTrunk = gi;
      this.$set(this.trunkResult, gi, null);
      try {
        const res = await axios.post(
          "telephony/createTrunk",
          {
            warehouseId: this.warehouseId,
            trunkName: gw.trunkName,
            host: gw.host,
          },
          { timeout: 70000 }
        );
        const c = res.data && (res.data.content || res.data);
        if (!c || c.ok === false) {
          // Cross-project name collision (registry 409) gets its own clear message.
          const msg = c && c.duplicateTrunk
            ? this.$t("tel_dg_trunk_dup", { trunk: c.duplicateTrunk, project: c.otherProject || "?" })
            : (c && c.reason === "not_supported"
                ? this.$t("tel_dg_check_unsupported")
                : ((c && c.error) || this.$t("tel_dg_trunk_fail")));
          this.$set(this.trunkResult, gi, { error: msg });
        } else {
          this.$set(this.trunkResult, gi, {
            mode: "create",
            healthy: !!c.healthy,
            checks: c.checks || [],
          });
        }
      } catch (e) {
        this.$set(this.trunkResult, gi, { error: this.$t("tel_dg_trunk_fail") });
      } finally {
        this.creatingTrunk = null;
      }
    },
    // Check a trunk's health from BOTH sides (VPS endpoint online + mini-PC
    // registered) — read-only, no config write. Shares the trunkResult[gi] panel.
    async checkTrunk(gi) {
      const gw = this.form.gateways[gi] || {};
      if (!gw.trunkName || !gw.host) return;
      this.checkingTrunk = gi;
      this.$set(this.trunkResult, gi, null);
      try {
        const res = await axios.post(
          "telephony/checkTrunk",
          {
            warehouseId: this.warehouseId,
            trunkName: gw.trunkName,
            host: gw.host,
          },
          { timeout: 40000 }
        );
        const c = res.data && (res.data.content || res.data);
        if (!c || c.ok === false) {
          this.$set(this.trunkResult, gi, {
            error: c && c.reason === "not_supported"
              ? this.$t("tel_dg_check_unsupported")
              : ((c && c.error) || this.$t("tel_dg_trunk_check_fail")),
          });
        } else {
          this.$set(this.trunkResult, gi, {
            mode: "check",
            healthy: !!c.healthy,
            checks: c.checks || [],
          });
        }
      } catch (e) {
        this.$set(this.trunkResult, gi, { error: this.$t("tel_dg_trunk_check_fail") });
      } finally {
        this.checkingTrunk = null;
      }
    },
    // Live auto-refresh of ONE gateway's dongle list. Only one gateway refreshes
    // at a time (a shared box; polling several would hammer it). Cleared on
    // toggle-off, on switching gateway, and on component destroy.
    toggleAutoRefresh(gi) {
      if (this.autoRefreshGi === gi) {
        this._stopAutoRefresh();
        return;
      }
      this._stopAutoRefresh();
      this.autoRefreshGi = gi;
      this.checkDongles(gi); // immediate first read
      this.autoRefreshTimer = setInterval(() => {
        // Don't stack requests: skip a tick if one is already in flight.
        if (this.checkingDongles === null && this.autoConfiguring === null) {
          this.checkDongles(gi);
        }
      }, 15000);
    },
    _stopAutoRefresh() {
      if (this.autoRefreshTimer) clearInterval(this.autoRefreshTimer);
      this.autoRefreshTimer = null;
      this.autoRefreshGi = null;
    },
    // Delete a gateway. MUST stop any live auto-refresh first: after the splice
    // the index shifts, and a leaked interval would keep polling a stale index
    // (→ undefined host → the CRM would hit the GLOBAL PBX with the secret).
    removeGateway(gi) {
      this._stopAutoRefresh();
      this.form.gateways.splice(gi, 1);
    },
    async checkDongles(gi) {
      // Guard: the gateway at this index may have vanished (deleted, or the
      // array was rebuilt by a save/scan reload while a refresh tick was queued).
      // Never poll a stale index — self-stop the refresh instead of mistargeting.
      if (!this.form.gateways || !this.form.gateways[gi]) {
        this._stopAutoRefresh();
        return;
      }
      this.checkingDongles = gi;
      this.$set(this.dongleStatus, gi, null);
      try {
        const gw = this.form.gateways[gi] || {};
        const res = await axios.post("telephony/checkDongles", {
          warehouseId: this.warehouseId,
          trunkName: gw.trunkName,
          host: gw.host, // live value in the form — targets THIS box even before save
        });
        const c = res.data && (res.data.content || res.data);
        if (!c || c.ok === false) {
          this.$set(this.dongleStatus, gi, {
            error: c && c.reason === "not_supported"
              ? this.$t("tel_dg_check_unsupported")
              : ((c && c.error) || this.$t("tel_dg_check_fail")),
          });
        } else if (c.reloading) {
          // A plug/unplug/dead-dongle reconfigure is in flight and chan_dongle is
          // briefly down. Show the spinner (not a false "no dongles") and re-poll
          // in 5s; the reconfigure settles in ~20-30s. Cap retries so a stuck box
          // eventually falls back to the normal (possibly empty) result.
          this.$set(this.dongleStatus, gi, {
            reloading: true, dongles: [],
            message: c.message || this.$t("tel_dg_reloading"),
          });
          this._dgReloadTries = (this._dgReloadTries || 0) + 1;
          if (this._dgReloadTries <= 12) {
            if (this._dgReloadTimer) clearTimeout(this._dgReloadTimer);
            this._dgReloadTimer = setTimeout(() => this.checkDongles(gi), 5000);
          } else {
            this._dgReloadTries = 0; // give up the wait; next manual click retries
          }
        } else {
          this._dgReloadTries = 0;
          if (this._dgReloadTimer) { clearTimeout(this._dgReloadTimer); this._dgReloadTimer = null; }
          const dongles = c.dongles || [];
          this.$set(this.dongleStatus, gi, { dongles });
          // Auto-fill dongleDevices from the live list so the backend can count
          // this gateway's channels precisely (Dongle/<device>-) instead of the
          // "any Dongle/" fallback. Saves the manager typing device names.
          const g = this.form.gateways[gi];
          if (g && dongles.length) {
            this.$set(g, "dongleDevices", dongles.map((d) => d.device).filter(Boolean));
            // Keep channels in sync with the real dongle count if unset/mismatched.
            if (!g.channels || g.channels < dongles.length) g.channels = dongles.length;
          }
        }
      } catch (e) {
        this.$set(this.dongleStatus, gi, { error: this.$t("tel_dg_check_fail") });
      } finally {
        this.checkingDongles = null;
      }
    },
    // Auto-detect + configure all dongles on the mini-PC (modeswitch → udev →
    // dongle.conf → reload), no SSH. Slow (~15–30s). On success, re-check to show
    // the fresh list (which also auto-fills dongleDevices).
    async autoConfigDongles(gi) {
      this.autoConfiguring = gi;
      this.$set(this.dongleStatus, gi, null);
      try {
        const gw = this.form.gateways[gi] || {};
        const res = await axios.post(
          "telephony/autoConfigDongles",
          { warehouseId: this.warehouseId, trunkName: gw.trunkName, host: gw.host },
          { timeout: 100000 }
        );
        const c = res.data && (res.data.content || res.data);
        if (!c || c.ok === false) {
          this.$set(this.dongleStatus, gi, {
            error: c && c.reason === "not_supported"
              ? this.$t("tel_dg_check_unsupported")
              : ((c && c.error) || this.$t("tel_dg_scan_fail")),
          });
        } else {
          this._toast(this.$t("tel_dg_scan_done", { n: c.count || 0 }), "success");
          // Show the fresh list (also auto-fills dongleDevices) + flag that the
          // new device mapping must be SAVED to persist (else the CRM keeps the
          // old dongleDevices → wrong busy-count after a renumber).
          await this.checkDongles(gi);
          this.$set(this.dongleStatus, gi, {
            ...(this.dongleStatus[gi] || {}),
            needsSave: true,
          });
        }
      } catch (e) {
        this.$set(this.dongleStatus, gi, { error: this.$t("tel_dg_scan_fail") });
      } finally {
        this.autoConfiguring = null;
      }
    },
    // Re-apply the chan_dongle voice patch (CLCC poll + auto DDSETEX) + rebuild +
    // reload on the mini-PC. Use when calls connect but are SILENT after a
    // chan_dongle recompile/update wiped the patch. Slow (a full `make` can take
    // a minute+); refuses if a call is active. On success, re-check the list.
    async repatchDongles(gi) {
      this.repatching = gi;
      try {
        const gw = this.form.gateways[gi] || {};
        const res = await axios.post(
          "telephony/repatchDongles",
          { warehouseId: this.warehouseId, trunkName: gw.trunkName, host: gw.host },
          { timeout: 200000 }
        );
        const c = res.data && (res.data.content || res.data);
        if (!c || c.ok === false) {
          const msg = c && c.reason === "not_supported"
            ? this.$t("tel_dg_check_unsupported")
            : ((c && c.error) || this.$t("tel_dg_repatch_fail"));
          this._toast(msg, "danger");
        } else {
          this._toast(
            this.$t("tel_dg_repatch_done", { v: c.voice || 0, n: c.dongles || 0 }),
            "success"
          );
          await this.checkDongles(gi);
        }
      } catch (e) {
        this._toast(this.$t("tel_dg_repatch_fail"), "danger");
      } finally {
        this.repatching = null;
      }
    },
    // Reset one stuck dongle, then re-check after a short wait (it re-registers
    // in ~30–90s — the "professional" version of the manual `dongle reset`).
    async resetDongle(gi, device) {
      this.resettingDongle = device;
      try {
        const gw = this.form.gateways[gi] || {};
        await axios.post("telephony/resetDongle", {
          device,
          warehouseId: this.warehouseId,
          trunkName: gw.trunkName,
          host: gw.host,
        });
        this._toast(this.$t("tel_dg_reset_done"), "success");
        // Re-check after the dongle has had time to re-register.
        setTimeout(() => this.checkDongles(gi), 35000);
      } catch (e) {
        this._toast(this.$t("tel_dg_reset_fail"), "danger");
      } finally {
        this.resettingDongle = null;
      }
    },
    // Open the designed dongle-test modal: preload the form + refresh the online
    // agent list so the dropdown is current. The chosen dongle rings the chosen
    // agent, who hears the SIM's outgoing call (normal ring = credit, "solde épuisé"
    // = dead SIM). Ground-truth SIM check.
    openDongleTestCall(gi, device) {
      this.dtc = {
        gi,
        device,
        country: (this.dongleTestCountries && this.dongleTestCountries[0]) || "MA",
        number: "",
      };
      this.$vm2.open("DongleTestModal");
    },
    // Create a SIP extension for the LOGGED-IN manager so they can hear the test in
    // their own browser. Same endpoint as an agent's extension (createAgentAccount),
    // targeting the current user's id. After it succeeds they must refresh once so
    // the softphone (App.vue) registers with the new extension.
    async generateMyExtension() {
      const uid = this.currentUser && this.currentUser._id;
      if (!uid) { this._toast(this.$t("tel_dg_testcall_fail") || "Erreur", "danger"); return; }
      this.dtcGenerating = true;
      try {
        const res = await axios.post("telephony/createAgentAccount", { userId: uid, device: "desktop" });
        const c = (res.data && (res.data.content || res.data)) || {};
        if (c && (c.ok || c.extension)) {
          // Reflect it locally so managerHasExtension flips without a full reload of
          // the store; the softphone still needs a page refresh to register.
          if (!this.currentUser.telephony) this.$set(this.currentUser, "telephony", {});
          this.$set(this.currentUser.telephony, "extension", c.extension || "set");
          this._toast(this.$t("tel_dg_testcall_gen_ok") || "Extension créée — rafraîchissez la page pour activer votre softphone", "success");
        } else {
          this._toast((c && (c.error || c.reason)) || (this.$t("tel_dg_testcall_gen_fail") || "Échec de création"), "danger");
        }
      } catch (e) {
        this._toast(this.$t("tel_dg_testcall_gen_fail") || "Échec de création", "danger");
      } finally {
        this.dtcGenerating = false;
      }
    },
    async submitDongleTest() {
      // Guard: no extension = no softphone to hear on → must generate it first.
      if (!this.managerHasExtension) { this._toast(this.$t("tel_dg_testcall_noext") || "Générez d'abord votre extension", "warning"); return; }
      const num = String(this.dtc.number || "").replace(/\s/g, "");
      if (!/^\+?\d{5,15}$/.test(num)) { this._toast(this.$t("tel_dg_testcall_badnum") || "Numéro invalide", "danger"); return; }
      this.dtcSubmitting = true;
      this.testingDongle = this.dtc.device;
      try {
        // AUTO-REGISTER the caller's OWN softphone first (CEO/manager testing from
        // Settings): the test rings THEIR extension, which must be registered or the
        // backend returns {notConnected}. GlobalSoftphone listens for this on the bus
        // and revives/registers the line (fresh secret + REGISTER, ~4s), so the user
        // doesn't have to open the softphone by hand before testing. Best-effort: if
        // there's no WebRTC line at all it resolves quickly and the backend's own
        // guard still reports a clear message.
        if (!softphoneState.registered) {
          await new Promise((resolve) => {
            let done = false;
            const finish = () => { if (!done) { done = true; resolve(); } };
            try { bus.$emit("softphone:ensure-ready", finish); } catch (e) { finish(); }
            setTimeout(finish, 6000); // safety cap
          });
        }
        const gw = this.form.gateways[this.dtc.gi] || {};
        const body = {
          device: this.dtc.device,
          number: num,
          country: this.dtc.country || "MA",
          warehouseId: this.warehouseId,
          trunkName: gw.trunkName,
          host: gw.host,
        };
        // No agentExtension → the backend rings the MANAGER's own softphone.
        const res = await axios.post("telephony/dongleTestCall", body);
        const c = (res.data && (res.data.content || res.data)) || {};
        if (c.ok) {
          this._toast(this.$t("tel_dg_testcall_sent") || "Appel lancé — décrochez sur le softphone", "success");
          this.$vm2.close("DongleTestModal");
        } else if (c.managerNoExtension) this._toast(this.$t("tel_dg_testcall_noext") || "Choisissez un agent connecté qui écoutera", "warning");
        else if (c.busy) this._toast(this.$t("tel_dg_testcall_busy") || "Agent déjà en appel — raccrochez avant de tester", "warning");
        else if (c.notConnected) this._toast(this.$t("tel_dg_testcall_offline") || "Le softphone de l'agent n'est pas connecté", "danger");
        else this._toast((c.message || c.error || c.reason) || (this.$t("tel_dg_testcall_fail") || "Échec de l'appel test"), "danger");
      } catch (e) {
        this._toast(this.$t("tel_dg_testcall_fail") || "Échec de l'appel test", "danger");
      } finally {
        this.dtcSubmitting = false;
        this.testingDongle = null;
      }
    },
    // Disable a no-credit SIM (park it out of the auto-dialer's call group) or
    // re-enable it after recharge. `enable=false` disables, `true` re-enables.
    async toggleDongleEnabled(gi, device, enable) {
      this.togglingDongle = device;
      try {
        const gw = this.form.gateways[gi] || {};
        const res = await axios.post("telephony/setDongleEnabled", {
          device, enabled: !!enable,
          warehouseId: this.warehouseId,
          trunkName: gw.trunkName,
          host: gw.host,
        });
        const c = res && res.data && (res.data.content || res.data);
        if (c && c.ok) {
          this._toast(enable ? this.$t("tel_dg_reenabled") : this.$t("tel_dg_disabled"), "success");
          setTimeout(() => this.checkDongles(gi), 4000); // reflect the new group
        } else {
          this._toast((c && c.message) || this.$t("tel_dg_reset_fail"), "danger");
        }
      } catch (e) {
        this._toast(this.$t("tel_dg_reset_fail"), "danger");
      } finally {
        this.togglingDongle = null;
      }
    },
    // Diagnostic: whole-warehouse inbound health. Per GoIP → is its trunk
    // registered in FreePBX; plus the shared active + online agents that ANY
    // inbound on this warehouse would ring, the sticky agent (if a caller is
    // given) and the Dial string the PBX would use — with warnings on misconfig.
    async runInboundTest() {
      if (!this.warehouseId) return;
      this.inboundTesting = true;
      this.inboundTestResult = null;
      try {
        const res = await axios.post("telephony/inboundHealth", {
          warehouseId: this.warehouseId,
          caller: this.inboundTestCaller || undefined,
        });
        this.inboundTestResult = res.data && (res.data.content || res.data);
      } catch (e) {
        this.inboundTestResult = { ok: false, error: e.message };
      } finally {
        this.inboundTesting = false;
      }
    },
    // Convert a 2-letter ISO country code (e.g. "TD") to its flag emoji (🇹🇩) by
    // mapping A-Z to the Unicode regional-indicator symbols. Returns "" if invalid.
    flagEmoji(code) {
      if (!code || typeof code !== "string" || code.length !== 2) return "";
      const cc = code.toUpperCase();
      if (!/^[A-Z]{2}$/.test(cc)) return "";
      return String.fromCodePoint(
        0x1f1e6 + (cc.charCodeAt(0) - 65),
        0x1f1e6 + (cc.charCodeAt(1) - 65)
      );
    },
    // Upload the predictive wait message (MP3): read the file → base64 → server
    // → BunnyCDN → store the returned URL on the form (persisted on Save).
    async onWaitMsgUpload(ev) {
      const file = ev && ev.target && ev.target.files && ev.target.files[0];
      if (!file) return;
      if (!/audio\/(mpeg|mp3)|\.mp3$/i.test(file.type + " " + file.name)) {
        this.$toast && this.$toast.warning(this.$t("tel_pd_mp3_only"));
        ev.target.value = "";
        return;
      }
      this.uploadingWaitMsg = true;
      try {
        let b64 = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.onerror = rej;
          r.readAsDataURL(file);
        });
        // The browser labels an MP3 as `data:audio/mpeg;base64,...` (or sometimes
        // audio/mp3 / octet-stream). The backend's bunnycdn.upload keys off the MIME
        // subtype and only accepts exactly `mp3`, so normalize the data-URL prefix to
        // audio/mp3 before sending — otherwise a valid MP3 is rejected as "not MP3".
        if (typeof b64 === "string") {
          b64 = b64.replace(/^data:audio\/[^;]+;base64,/, "data:audio/mp3;base64,")
                   .replace(/^data:application\/octet-stream;base64,/, "data:audio/mp3;base64,");
        }
        const resp = await axios.post("telephony/uploadWaitMessage", { audio: b64 });
        const c = resp.data && (resp.data.content || resp.data);
        if (c && c.ok && c.url) {
          this.form.predictiveDialer.waitMessageUrl = c.url;
          // Persist immediately so the URL survives a page refresh — the upload only
          // pushes the file to the CDN; without a save the URL lives only in memory
          // and a reload reloads the (URL-less) backend settings. Auto-save avoids
          // the "I refreshed and the message disappeared" trap.
          try { await this.save(true); } catch (e) { /* the toast below still confirms upload */ }
          this.$toast && this.$toast.success(this.$t("tel_pd_uploaded"));
          // Push the new audio to the PBX box(es) so Asterisk transcodes it to the
          // per-project WAV + MOH right away (not awaited — the upload is already saved).
          this.pushWaitMsgToBoxes();
        } else {
          this.$toast && this.$toast.warning((c && c.error) || this.$t("tel_pd_upload_failed"));
        }
      } catch (e) {
        this.$toast && this.$toast.warning(this.$t("tel_pd_upload_failed"));
      } finally {
        this.uploadingWaitMsg = false;
        ev.target.value = "";
      }
    },
    // Apply the built-in French default voice: point waitMessageUrl at the default
    // CDN file + persist. The player then appears so the manager can hear it, and
    // the VPS fetches the SAME url like any custom message.
    async applyDefaultWaitMsg() {
      if (!this.form || !this.form.predictiveDialer) return;
      this.form.predictiveDialer.waitMessageUrl = this.defaultWaitMsgUrl;
      try { await this.save(true); } catch (e) { /* ignore */ }
      this.$toast && this.$toast.success(this.$t("tel_pd_uploaded"));
      this.pushWaitMsgToBoxes();
    },
    // Push the saved wait message to the PBX box(es): crm-ext.php fetchWaitMsg
    // transcodes it to the PER-PROJECT WAV + MOH (crm-waitmsg-<project> /
    // crm-hold-<project>), so on a shared box no project plays another's greeting.
    // Called automatically after upload/apply, and from the manual "Sync" button
    // (for when the box was offline during upload). Best-effort + a toast.
    async pushWaitMsgToBoxes() {
      this.syncingWaitMsg = true;
      try {
        const res = await axios.post("telephony/syncWaitMessage", {}, { timeout: 60000 });
        const c = (res.data && (res.data.content || res.data)) || {};
        if (c.ok && c.synced) {
          this.$toast && this.$toast.success(this.$t("tel_pd_waitmsg_synced"));
        } else if (c.ok) {
          // Partial — some box failed (offline / wrong secret). Name the first error.
          const bad = (c.results || []).find((r) => !r.ok);
          this.$toast && this.$toast.warning(
            this.$t("tel_pd_waitmsg_sync_partial") + (bad && bad.error ? (" — " + bad.error) : "")
          );
        } else if (c.reason !== "not_supported") {
          this.$toast && this.$toast.warning((c.error) || this.$t("tel_pd_waitmsg_sync_failed"));
        }
      } catch (e) {
        this.$toast && this.$toast.warning(this.$t("tel_pd_waitmsg_sync_failed"));
      } finally {
        this.syncingWaitMsg = false;
      }
    },
    // Build the one-time onboarding command for a NEW box, from the gateway's host
    // + the panel inputs. The CRM does NOT SSH the box and NO secret is embedded —
    // the secret is a shell placeholder the operator fills from telephonyConfig, so
    // nothing sensitive touches the browser/DOM. projectId comes from the backend
    // telephonyConfig (exposed read-only via vpsHealth/config), else defaults to the
    // current project label; the operator can edit the printed command anyway.
    onboardCommand(g) {
      const host = (g && g.host && String(g.host).trim()) || "<box-ip>";
      const user = (this.onboardUser || "root").trim();
      // ALWAYS pass --project and --api explicitly: when a box is re-purposed from another project the
      // script's defaults would silently onboard it to the wrong one. Real values come from this
      // install's telephonyConfig (via getSettings); placeholders only if the API did not answer.
      const proj = this.projectMeta.projectId || (this.vpsHealth && this.vpsHealth.project) || "<projectId>";
      const api = this.projectMeta.scriptsApiUrl || (this.vpsHealth && this.vpsHealth.api) || "<apiUrl>";
      let cmd = "cd eshipi-API/docs/telephony\n";
      cmd += "CRM_WEBHOOK_SECRET=<webhook-secret> ./onboard-box.sh";
      cmd += " --host " + host;
      cmd += " --user " + user;
      if (this.onboardKey && this.onboardKey.trim()) cmd += " --key " + this.onboardKey.trim();
      if (this.onboardSudo) cmd += " --sudo";
      cmd += " --project " + proj;
      cmd += " --api " + api;
      // chan_dongle (compile + voice patch) — the slow OS-level piece; opt-in.
      if (this.onboardWithDongle) cmd += " --with-dongle";
      return cmd;
    },
    copyOnboardCommand(g) {
      const text = this.onboardCommand(g);
      const done = () => { this.onboardCopied = true; setTimeout(() => { this.onboardCopied = false; }, 1500); };
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(done).catch(done);
        } else {
          const ta = document.createElement("textarea");
          ta.value = text; document.body.appendChild(ta); ta.select();
          try { document.execCommand("copy"); } catch (e) { /* ignore */ }
          document.body.removeChild(ta); done();
        }
      } catch (e) { done(); }
    },
    // Clear the wait message entirely → back to STATE A (no message set). A cleared
    // message means the client hears no greeting (just the hold music).
    async removeWaitMsg() {
      if (this.form && this.form.predictiveDialer) this.form.predictiveDialer.waitMessageUrl = "";
      try { await this.save(true); } catch (e) { /* ignore */ }
    },
    openExtModal() {
      this.$vm2.open("ExtensionsModal");
      // Load fresh each time the modal opens.
      this.checkExtensions();
    },
    async checkExtensions() {
      if (!this.warehouseId) return;
      this.checkingExts = true;
      try {
        const res = await axios.post("telephony/checkExtensions", { warehouseId: this.warehouseId });
        this.extResult = res.data && (res.data.content || res.data);
      } catch (e) {
        this.extResult = { ok: false, error: e.message };
      } finally {
        this.checkingExts = false;
      }
    },
    // Generate (or recreate) the SIP extension for an agent, then refresh the list.
    async genExtension(a) {
      if (!a || !a.userId || this.extActionId) return;
      this.extActionId = a.userId;
      try {
        const res = await axios.post("telephony/createAgentAccount", { userId: a.userId });
        const c = res.data && (res.data.content || res.data);
        if (c && (c.ok || c.extension)) {
          this.$toast && this.$toast.success(this.$t("tel_ext_generated"));
        } else {
          this.$toast && this.$toast.warning((c && (c.error || c.message)) || this.$t("tel_ext_gen_failed"));
        }
      } catch (e) {
        this.$toast && this.$toast.warning(this.$t("tel_ext_gen_failed"));
      } finally {
        this.extActionId = null;
        await this.checkExtensions();
      }
    },
    // Enable / disable an agent's line (telephony.active), then refresh.
    async toggleExtActive(e, active) {
      if (!e || !e.userId || this.extActionId) return;
      this.extActionId = e.userId;
      try {
        await axios.post("telephony/setAgentExtensionActive", { userId: e.userId, active });
        this.$toast && this.$toast.success(this.$t(active ? "tel_ext_activated" : "tel_ext_deactivated"));
      } catch (err) {
        this.$toast && this.$toast.warning(this.$t("tel_ext_toggle_failed"));
      } finally {
        this.extActionId = null;
        await this.checkExtensions();
      }
    },
    // Only one VPS-panel result visible at a time (Test / Check VPS / Sync config
    // share the same area) — clear the others when a new one runs.
    _clearVpsPanels() {
      this.testResult = null;
      this.vpsHealth = null;
      this.syncResult = null;
    },
    // Normalize any {healthy, errors, checks[], error} result (trunk / mini-PC) into
    // the unified health-card shape { headerLevel, title, rows } — same look as the
    // VPS panel. `titles` = { ok, issues } text; `okTitle`/`issuesTitle` chosen by errors.
    _healthCard(res, okTitle, issuesTitle) {
      if (!res) return null;
      if (res.error) return { headerLevel: "error", title: res.error, rows: [] };
      const rows = (res.checks || []).map((c) => ({
        level: c.level || (c.ok ? "ok" : "error"),
        text: c.message,
        detail: c.detail || "",
      }));
      const hasErr = rows.some((r) => r.level === "error");
      const hasWarn = rows.some((r) => r.level === "warn");
      const nErr = res.errors != null ? res.errors : rows.filter((r) => r.level === "error").length;
      return {
        headerLevel: hasErr ? "error" : (hasWarn ? "warn" : "ok"),
        title: (res.healthy && !hasErr && !hasWarn) ? okTitle : (issuesTitle + (nErr ? " (" + nErr + ")" : "")),
        rows,
      };
    },
    async testConnection() {
      this._clearVpsPanels();
      this.testing = true;
      try {
        // Save first so the server tests the persisted (encrypted) creds.
        await this.save(true);
        const res = await axios.post("telephony/testConnection", { warehouseId: this.warehouseId });
        this.testResult = res.data && (res.data.content || res.data);
      } catch (e) {
        this.testResult = { ok: false, error: e.message };
      } finally {
        this.testing = false;
      }
    },
    // Full health of the GLOBAL VPS hub: ARI reachable+auth, trunk to the
    // mini-PC online, WebRTC agents registered, WSS cert valid. Same
    // {healthy, errors, checks[]} shape as the mini-PC health.
    async checkVpsHub() {
      this._clearVpsPanels();
      this.checkingVps = true;
      try {
        const res = await axios.post(
          "telephony/checkVpsHub",
          { warehouseId: this.warehouseId },
          { timeout: 30000 }
        );
        const c = res.data && (res.data.content || res.data);
        if (!c || c.ok === false) {
          this.vpsHealth = { error: (c && c.error) || this.$t("tel_vps_check_fail") };
        } else {
          this.vpsHealth = { healthy: !!c.healthy, errors: c.errors || 0, checks: c.checks || [] };
        }
      } catch (e) {
        this.vpsHealth = { error: this.$t("tel_vps_check_fail") };
      } finally {
        this.checkingVps = false;
      }
    },
    // Push telephonyConfig.js's API url + webhook secret + projectId into every box's
    // /etc/asterisk/crm-box.conf (crm-ext.php + all crm-*.sh read it), so changing any
    // of those values propagates automatically — no manual sed on each box.
    async syncConfigToBoxes() {
      this._clearVpsPanels();
      this.syncingConfig = true;
      try {
        const res = await axios.post("telephony/syncConfig", {}, { timeout: 40000 });
        const c = res.data && (res.data.content || res.data);
        if (!c || c.ok === false) {
          this.syncResult = { synced: false, results: [{ host: "", ok: false, error: (c && (c.error || c.reason)) || this.$t("tel_sync_config_fail") }] };
        } else {
          this.syncResult = { synced: !!c.synced, results: c.results || [] };
        }
      } catch (e) {
        this.syncResult = { synced: false, results: [{ host: "", ok: false, error: this.$t("tel_sync_config_fail") }] };
      } finally {
        this.syncingConfig = false;
      }
    },
    async save(silent) {
      if (!this.warehouseId) return;
      // Client-side pre-check: no duplicate trunk name WITHIN this warehouse
      // (the backend also guards cross-warehouse — it's the only side that can
      // see other warehouses' trunks).
      const names = (this.form.gateways || [])
        .map((g) => (g.trunkName || "").trim().toLowerCase())
        .filter(Boolean);
      const dup = names.find((n, i) => names.indexOf(n) !== i);
      if (dup) {
        this._toast(this.$t("tel_gw_dup_here"), "danger");
        return;
      }
      this.saving = true;
      try {
        const res = await axios.post("telephony/updateSettings", {
          warehouseId: this.warehouseId,
          telephony_settings: this.form,
        });
        // Backend rejects a trunk already owned by another warehouse (would
        // break call isolation) → surface which warehouse owns it.
        const c = res && res.data && res.data.content;
        if (c && c.duplicateTrunk) {
          this._toast(
            this.$t("tel_gw_dup_other", { trunk: c.duplicateTrunk, wh: c.otherWarehouse || "?" }),
            "danger"
          );
          return;
        }
        // The API answers HTTP 200 with { status, content } — a refused save (403 Forbidden,
        // 500…) must NOT be reported as saved.
        const body = res && res.data;
        if (!body || (body.status && Number(body.status) >= 400) || (c && c.ok === false)) {
          const reason = typeof c === "string" ? c : (c && (c.error || c.message)) || "";
          this._toast(this.$t("tel_save_failed") + (reason ? ": " + reason : ""), "danger");
          return;
        }
        if (silent !== true) this._toast("Enregistré", "success");
        await this.loadSettings();
      } catch (e) {
        // A 400 with duplicateTrunk lands here too (axios throws on 4xx).
        const c = e && e.response && e.response.data && e.response.data.content;
        if (c && c.duplicateTrunk) {
          this._toast(
            this.$t("tel_gw_dup_other", { trunk: c.duplicateTrunk, wh: c.otherWarehouse || "?" }),
            "danger"
          );
        } else {
          this._toast("Save failed: " + e.message, "danger");
        }
      } finally {
        this.saving = false;
      }
    },
    _toast(msg, type) {
      // vue-toastification (this.$toast) — the app's toast system.
      const t = this.$toast;
      if (!t) return;
      if (type === "danger") return t.error(msg);
      if (type === "success") return t.success(msg);
      return typeof t.info === "function" ? t.info(msg) : t(msg);
    },
  },
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity .18s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }

/* ── Section tabs ─────────────────────────────────────────────────── */
.tel-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 5px;
  background: #f1f5f9;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}
.tel-tab {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 16px;
  border-radius: 9px;
  font-size: 13.5px;
  font-weight: 600;
  color: #64748b;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background .15s, color .15s, box-shadow .15s;
  white-space: nowrap;
}
.tel-tab:hover { color: #334155; background: #e2e8f0; }
.tel-tab--active {
  color: #0369a1;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, .08);
}
.tel-tabpane { animation: telTabIn .18s ease; }
@keyframes telTabIn { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .tel-tabpane { animation: none; } }

/* ── "How it works" call-flow explainer ─────────────────────────── */
.tel-flow {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  background:
    radial-gradient(120% 100% at 0% 0%, #f8fbff 0%, #ffffff 45%);
  box-shadow: 0 1px 2px rgba(22,32,43,.04), 0 8px 26px rgba(37,99,201,.06);
  padding: 20px 22px 22px;
}
.tel-flow__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; margin-bottom: 14px; }
.tel-flow__eyebrow {
  font-size: 11px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase;
  color: #2563c9; margin-bottom: 5px;
}
.tel-flow__title { font-size: 16px; font-weight: 700; color: #16202b; margin: 0 0 4px; letter-spacing: -.01em; }
.tel-flow__sub { font-size: 12.5px; color: #5a6b7b; margin: 0; max-width: 60ch; line-height: 1.55; }
.tel-flow__close {
  flex: none; width: 26px; height: 26px; border-radius: 8px; border: 0;
  background: #eef2f6; color: #64748b; font-size: 17px; line-height: 1; cursor: pointer;
}
.tel-flow__close:hover { background: #e2e8f0; color: #334155; }
.tel-flow__stage {
  border: 1px solid #eef2f6; border-radius: 12px; background: #ffffff;
  padding: 8px 10px 4px; margin-bottom: 16px;
}
.tel-flow__cards { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 640px) { .tel-flow__cards { grid-template-columns: 1fr; } }
.tel-flow__card {
  border: 1px solid #e6e2f4; border-top: 3px solid #7c3aed; border-radius: 12px;
  background: #ffffff; padding: 12px 14px;
}
.tel-flow__card-h { font-size: 12.5px; font-weight: 700; color: #6d28d9; margin-bottom: 4px; }
.tel-flow__card p { font-size: 12px; color: #5a6b7b; margin: 0; line-height: 1.55; }
.tel-flow__tip { font-size: 11.5px; color: #64748b; margin: 12px 0 0; }
@media (prefers-color-scheme: dark) {
  .tel-flow { background: radial-gradient(120% 100% at 0% 0%, #16202b 0%, #131c25 45%); border-color: #26343f; box-shadow: none; }
  .tel-flow__title { color: #e8eef4; }
  .tel-flow__sub, .tel-flow__card p { color: #a3b3c2; }
  .tel-flow__close { background: #1d2832; color: #8494a3; }
  .tel-flow__close:hover { background: #26343f; color: #cbd5e1; }
  .tel-flow__stage { background: #0e151c; border-color: #1d2832; }
  .tel-flow__card { background: #131c25; border-color: #2b2440; border-top-color: #a78bfa; }
  .tel-flow__card-h { color: #c4b5fd; }
  .tel-flow__tip { color: #8494a3; }
}

.lbl { @apply block text-xs font-medium text-gray-600 mb-1; }
.inp { @apply w-full border rounded-lg px-3 py-2 text-sm; }
/* Tailwind 1.x can't @apply variants (hover:/disabled:) → those states are plain CSS */
.btn-primary { @apply bg-green-600 text-white text-sm px-5 py-2 rounded-lg; }
.btn-primary:hover { background-color: #15803d; }
.btn-primary:disabled { opacity: 0.5; }
.btn-secondary { @apply bg-gray-100 text-gray-700 text-sm px-4 py-2 rounded-lg; }
.btn-secondary:hover { background-color: #e5e7eb; }
.btn-secondary:disabled { opacity: 0.5; }
.sky-button {
  border-width: 1px;
  border-color: #0284c7;
  background-color: #0284c7;
  color: #ffffff; /* Tailwind text-white isn't emitted in this build → set it here */
}

.sky-button:hover {
  background-color: #0369a1;
  border-color: #0369a1;
}
.rose-button {
  border-width: 1px;
  border-color: #fda4af; /* rose-300 */
  background-color: #ffffff; /* white */
  color: #be123c; /* rose-700 */
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05); /* shadow-sm */
}

.rose-button:hover {
  background-color: #fff1f2; /* rose-50 */
}

.violet-button {
  border-width: 1px;
  border-color: #7c3aed; /* violet-600 */
  background-color: #7c3aed; /* violet-600 */
  color: #ffffff; /* Tailwind text-white isn't emitted in this build → set it here */
}

.violet-button:hover {
  background-color: #6d28d9; /* violet-700 */
  border-color: #6d28d9; /* violet-700 */
}

</style>
